import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Slider from "@mui/material/Slider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

// Icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";

import {observer} from "mobx-react-lite";
import {ApplicationStore} from "../../librettoEditor/store/ApplicationStore";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import { LayoutService } from "../../librettoEditor/services/LayoutService";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import {ScriptLayerStore} from "../../librettoEditor/store/ScriptLayerStore";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
import {AudioClip, ScriptLayerClip} from "../../engine/clips";
import type { TransitionConfig as EngineTransitionConfig } from "../../engine/clips/script";
import {VideoClip} from "../../engine/clips/video/video";
import {ImageClip} from "../../engine/clips/image/image";
import {formatDurationBrief} from "../../utils/utils";
import {reaction} from "mobx";
import {VideoStable} from "@mui/icons-material";
import {useAssetStore} from "../../storage/AssetStore";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { CompositionEventsEnum } from "../../librettoEditor/CompositionEvents";
import { Timestamp } from "../../engine/models";
import { AudioWaveformIcon } from "../NewHome/ProjectThumbnail";
import Tooltip from "@mui/material/Tooltip";
import {
    BUILTIN_LAYOUT_PACK,
    DEFAULT_LAYOUT_PACK_ID,
    formatLayoutRef,
    getLayoutKindFromLayoutId,
    getLayoutTemplate,
    normalizeLayoutId,
    parseLayoutRef,
    parseMulticamVariantKeyFromLayoutId,
    type CanvasAspectPreset,
    type MulticamVariant,
} from "../../librettoEditor/layouts/LayoutPacks";
import { LayoutPacksStore } from "../../state/LayoutPacksStore";
import { CreateClipForComposition } from "./utils";

type TransitionType = "smart" | "fade";

type TransitionEdgeState = {
    type: TransitionType;
    duration: string;
    easing: string;
    addFades: boolean;
};

const TRANSITION_OPTIONS = [
    { value: "smart", label: "Smart transition" },
    { value: "fade", label: "Fade" },
];

const EASING_OPTIONS = [
    { value: "natural", label: "Natural" },
    { value: "speed_up", label: "Speed up" },
    { value: "slow_down", label: "Slow down" },
    { value: "elastic_out", label: "Elastic out" },
    { value: "bounce_out", label: "Bounce out" },
    { value: "swing", label: "Swing" },
    { value: "impulse", label: "Impulse" },
    { value: "overshoot", label: "Overshoot" },
    { value: "none", label: "None" },
];

const DEFAULT_TRANSITION_DURATION = "0.8";
const DEFAULT_SMART_EASING = "natural";

type ClipAnimationKind = "custom" | "zoom_pan";

type ClipAnimationEntry = {
    id: string;
    kind: ClipAnimationKind;
    startSec: number;
    endSec: number;
    easing: string;
};

const CLIP_ANIMATION_STORE_KEY = "__librettoClipAnimations";
const CLIP_ANIMATION_DEFAULT_DURATION_SEC = 5;
const CLIP_ANIMATION_MIN_DURATION_SEC = 0.1;

const clampVolumePct = (value: number): number => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(400, n));
};

const normalizeVolumePct = (value: number): number => {
    return Math.round(clampVolumePct(value));
};

const createClipAnimationId = (): string => {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch {}
    return `anim_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
};

const getClipTimelineBoundsSec = (targetClip: any): { startSec: number; endSec: number } => {
    const startSec = Number(targetClip?.start?.seconds ?? 0);
    const timelineDurationSec = Number(targetClip?.timelineDuration?.seconds ?? Number.NaN);
    const fallbackEndSec = Number(targetClip?.stop?.seconds ?? startSec);

    const endSec = Number.isFinite(timelineDurationSec)
        ? startSec + Math.max(0, timelineDurationSec)
        : Math.max(startSec, fallbackEndSec);

    return {
        startSec: Number.isFinite(startSec) ? startSec : 0,
        endSec: Number.isFinite(endSec) ? endSec : Math.max(0, startSec),
    };
};

const formatAnimationTimestamp = (seconds: number): string => {
    const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const hours = Math.floor(safe / 3600);
    const mins = Math.floor((safe % 3600) / 60);
    const secs = (safe % 60).toFixed(1).padStart(4, "0");
    if (hours > 0) {
        return `${hours}:${String(mins).padStart(2, "0")}:${secs}`;
    }
    return `${mins}:${secs}`;
};

const parseAnimationTimestamp = (value: string): number | null => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;

    if (!raw.includes(":")) {
        const sec = Number(raw);
        return Number.isFinite(sec) ? sec : null;
    }

    const parts = raw.split(":").map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2 || parts.length > 3) return null;

    const sec = Number(parts[parts.length - 1]);
    const min = Number(parts[parts.length - 2]);
    const hour = parts.length === 3 ? Number(parts[0]) : 0;
    if (![sec, min, hour].every((n) => Number.isFinite(n) && n >= 0)) return null;

    return hour * 3600 + min * 60 + sec;
};

const hasAnimationOverlap = (
    animations: ClipAnimationEntry[],
    candidateStartSec: number,
    candidateEndSec: number,
    skipAnimationId?: string,
): boolean => {
    const start = Math.min(candidateStartSec, candidateEndSec);
    const end = Math.max(candidateStartSec, candidateEndSec);
    return animations.some((anim) => {
        if (skipAnimationId && anim.id === skipAnimationId) return false;
        return start < anim.endSec && end > anim.startSec;
    });
};

const sanitizeClipAnimations = (
    rawAnimations: unknown,
    bounds: { startSec: number; endSec: number },
): ClipAnimationEntry[] => {
    const list = Array.isArray(rawAnimations) ? rawAnimations : [];
    const easingValues = new Set(EASING_OPTIONS.map((o) => String(o.value)));
    const sanitized: ClipAnimationEntry[] = [];

    for (const item of list) {
        if (!item || typeof item !== "object") continue;
        const rec = item as any;
        const id = String(rec.id ?? "").trim();
        if (!id) continue;

        const rawKind = String(rec.kind ?? "custom").trim();
        const kind: ClipAnimationKind = rawKind === "zoom_pan" ? "zoom_pan" : "custom";
        const easing = easingValues.has(String(rec.easing ?? ""))
            ? String(rec.easing)
            : DEFAULT_SMART_EASING;

        const startSecRaw = Number(rec.startSec ?? Number.NaN);
        const endSecRaw = Number(rec.endSec ?? Number.NaN);
        if (!Number.isFinite(startSecRaw) || !Number.isFinite(endSecRaw)) continue;

        const startSec = Math.max(bounds.startSec, Math.min(bounds.endSec, startSecRaw));
        const endSec = Math.max(bounds.startSec, Math.min(bounds.endSec, endSecRaw));
        if (endSec - startSec < CLIP_ANIMATION_MIN_DURATION_SEC) continue;

        sanitized.push({
            id,
            kind,
            startSec,
            endSec,
            easing,
        });
    }

    sanitized.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
    return sanitized;
};

function getCanvasAspectPreset(width: number, height: number): CanvasAspectPreset {
    const w = Number(width);
    const h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return "horizontal";
    const ratio = w / h;
    if (Math.abs(ratio - 1) < 0.06) return "square";
    return ratio < 1 ? "vertical" : "horizontal";
}

function getLayoutKind(name: string | null | undefined): string | null {
    const parsed = parseLayoutRef(name);
    if (!parsed) return null;
    return getLayoutKindFromLayoutId(parsed.layoutId);
}

function getLayoutDisplayName(name: string | null | undefined): string | null {
    const parsed = parseLayoutRef(name);
    if (!parsed) return null;
    const pack = LayoutPacksStore.getPack(parsed.packId) ?? BUILTIN_LAYOUT_PACK;
    const kind = getLayoutKindFromLayoutId(parsed.layoutId);
    if (!kind) return null;
    const templateId = kind === "multicam" ? "multicam" : kind;
    const tmpl = getLayoutTemplate(pack, templateId);
    return tmpl?.title ?? kind.charAt(0).toUpperCase() + kind.slice(1);
}

function parseMulticamVariantKey(name: string | null | undefined): string | null {
    const parsed = parseLayoutRef(name);
    if (!parsed) return null;
    return parseMulticamVariantKeyFromLayoutId(parsed.layoutId);
}

function isLayoutSelectedForOption(sceneLayout: string | null, optionLayoutId: string, activePackId: string): boolean {
    const parsed = parseLayoutRef(sceneLayout);
    if (!parsed) return false;
    const scenePackId = String(parsed.packId ?? DEFAULT_LAYOUT_PACK_ID);
    if (scenePackId !== String(activePackId ?? DEFAULT_LAYOUT_PACK_ID)) return false;

    const sceneLayoutId = normalizeLayoutId(parsed.layoutId);
    const optionId = normalizeLayoutId(optionLayoutId);
    const sceneKind = getLayoutKindFromLayoutId(sceneLayoutId);
    const optKind = getLayoutKindFromLayoutId(optionId);
    if (!optKind || !sceneKind) return false;

    // The main "Multicam" tile represents multiple variants; treat any multicam:* as selected.
    if (optKind === "multicam") return sceneKind === "multicam";

    // For all other layouts, match the full layout id (allows multiple templates per type in a pack).
    return sceneLayoutId === optionId;
}

function muxPlaybackIdToPreviewUrls(playbackId: string, preset: CanvasAspectPreset) {
    const id = String(playbackId);
    const previewUrl = `https://stream.mux.com/${id}.m3u8`;
    const { width, height } = (() => {
        if (preset === "vertical") return { width: 121, height: 214 };
        if (preset === "square") return { width: 170, height: 170 };
        return { width: 214, height: 121 };
    })();
    const posterUrl = `https://image.mux.com/${id}/thumbnail.png?width=${width}&height=${height}&time=1`;
    return { previewUrl, posterUrl };
}

function previewToUrls(
    preview: { muxPlaybackId?: string; thumbnailUrl?: string } | null | undefined,
    preset: CanvasAspectPreset
): { previewUrl?: string; posterUrl?: string } | null {
    if (!preview) return null;
    const muxPlaybackId = String((preview as any).muxPlaybackId ?? "").trim();
    if (muxPlaybackId) return muxPlaybackIdToPreviewUrls(muxPlaybackId, preset);
    const thumb = String((preview as any).thumbnailUrl ?? "").trim();
    if (thumb) return { posterUrl: thumb };
    return null;
}

function getLayoutPackStorageKey(projectId: string | null | undefined): string {
    const pid = String(projectId ?? "").trim() || "unknown";
    return `libretto.layoutPack.${pid}`;
}

function loadStoredLayoutPackId(projectId: string | null | undefined): string | null {
    try {
        const raw = window.localStorage.getItem(getLayoutPackStorageKey(projectId));
        const id = String(raw ?? "").trim();
        return id || null;
    } catch {}
    return null;
}

function persistLayoutPackId(projectId: string | null | undefined, packId: string): void {
    try {
        if (!projectId) return;
        window.localStorage.setItem(getLayoutPackStorageKey(projectId), String(packId ?? ""));
    } catch {}
}

const PaperPanel: React.FC<{
    title: string;
    children: React.ReactNode;
    fill?: boolean; // when true, take remaining column space
    headerContent?: React.ReactNode;
    showDivider?: boolean;
}> = ({title, children, fill, headerContent, showDivider = true}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            background: "var(--app-bg-surface)",
            border: "0.5px solid var(--app-border-subtle)",
            borderRadius: "12px",
            px: 1,
            py: 1,
            ...(fill ? {flex: 1, minHeight: 0} : {}),
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 0.5,
                py: 0.5,
            }}
        >
            <Typography sx={{fontSize: 14, fontWeight: 600, userSelect: 'none'}}>{title}</Typography>
            {headerContent}
        </Box>
        {showDivider && <Divider sx={{ my: 0.5 }} />}
        <Box sx={{...(fill ? {overflow: "auto"} : {})}}>{children}</Box>
    </Box>
);

type LayerRow = {
    id: string;
    label: string;
    visible: boolean;
    muted?: boolean;
    kind: "scriptSource" | "clip";
    scriptSourceIndex?: number;
    clipId?: string;
    thumbnailId?: string | null;
    mediaKind?: "visual" | "audio";
};

const LayersPanel: React.FC<{
    playheadSceneIndex: number | null;
}> = ({ playheadSceneIndex }) => {
    const [layers, setLayers] = useState<LayerRow[]>([]);
    const [thumbnailUrlsByRowId, setThumbnailUrlsByRowId] = useState<Record<string, string>>({});
    const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
    const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);
    const draggingRowIdRef = useRef<string | null>(null);
    const { GetThumbnailUrl } = useAssetStore();
    const prevGainByRowIdRef = useRef<Record<string, number>>({});
    const prevMultiplierByRowIdRef = useRef<Record<string, number>>({});

    useEffect(() => {
	        const disposer = reaction(
		            () => ({
		                scriptClipId: CompositionService.getScriptLayerClip()?.id,
		                sceneIndex: playheadSceneIndex,
		                canvasW: (ApplicationStore as any)?.canvasResolution?.[0],
		                canvasH: (ApplicationStore as any)?.canvasResolution?.[1],
                        layerOrderVersion: ScriptLayerStore.layoutVisualOrderVersion,
		                // Re-render when any clip update happens (best-effort, avoids needing fine-grained deps).
		                clipsVersion: ScriptLayerStore.cutsVersion,
		                visualPropsVersion: ScriptLayerStore.visualPropsVersion,
		                clipsCount: Object.keys(CompositionStateStore.clips ?? {}).length,
		            }),
		            () => {
		                const scriptClip: any = CompositionService.getScriptLayerClip();
		                const comp: any = CompositionService.composition;
		                if (!comp || !scriptClip || playheadSceneIndex == null) {
		                    setLayers([]);
		                    return;
		                }

		                const cw = Number((ApplicationStore as any)?.canvasResolution?.[0] ?? comp?.settings?.width ?? comp?.width ?? 1920);
		                const ch = Number((ApplicationStore as any)?.canvasResolution?.[1] ?? comp?.settings?.height ?? comp?.height ?? 1080);
		                const ratio = ch > 0 ? cw / ch : 16 / 9;
		                const aspectPreset = Math.abs(ratio - 1) < 0.06 ? "square" : ratio < 1 ? "vertical" : "horizontal";

                let sceneKey = "__start__";
                let sceneStart = 0;
                let sceneEnd = 0;
                try {
                    const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
                    const s =
                        scenes.find((x: any) => Number(x?.index) === Number(playheadSceneIndex)) ??
                        scenes[playheadSceneIndex];
                    if (s) {
                        sceneKey = s?.startSplitId ? String(s.startSplitId) : "__start__";
                        sceneStart = Number(s?.timelineStartSec ?? 0);
                        sceneEnd = Number(s?.timelineEndSec ?? sceneStart);
                    }
                } catch {}

                const rows: LayerRow[] = [];

                // Script-layer sources
                try {
                    const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
                    const entries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
                    const videoSources: any[] = typeof scriptClip.getVideoSources === "function" ? scriptClip.getVideoSources() : [];
                    const audioEntries: any[] = typeof scriptClip.getAudioEntries === "function" ? scriptClip.getAudioEntries() : [];
                    const count = Math.max(entries.length, videoSources.length);

                    for (let sourceIndex = 0; sourceIndex < count; sourceIndex++) {
                        const entry = entries?.[sourceIndex];
                        const input = entry?.input;
                        const cfg = input ? cfgs.find((c: any) => c?.input === input) : undefined;
                        const trackId = String(cfg?.assetId ?? "");
                        const track = trackId ? ProjectAssetsStore.projectTracks?.find((t: any) => String(t?.id ?? t?.trackId ?? "") === trackId) : null;
                        const name = String(cfg?.name ?? track?.title ?? `Source ${sourceIndex + 1}`);
                        const vis = scriptClip.getSceneVisualProperties?.(playheadSceneIndex, sourceIndex);
                        const alpha = Number(vis?.alpha ?? 1);
                        const visible = !(Number.isFinite(alpha) && alpha <= 0.001);
                        const audioEntryIndex = input ? audioEntries.findIndex((e: any) => e?.input === input) : -1;
                        const audioEntry = audioEntryIndex >= 0 ? audioEntries[audioEntryIndex] : undefined;
                        const baseGain = Number(audioEntry?.gain ?? cfg?.gain ?? 1);
                        const multiplier = Number(
                            typeof scriptClip.getEffectiveAudioSourceGainMultiplier === "function"
                                ? scriptClip.getEffectiveAudioSourceGainMultiplier(playheadSceneIndex, audioEntryIndex >= 0 ? audioEntryIndex : sourceIndex)
                                : 1,
                        );
                        const effectiveGain = (Number.isFinite(baseGain) ? baseGain : 1) * (Number.isFinite(multiplier) ? multiplier : 1);
                        const muted = Number.isFinite(effectiveGain) && effectiveGain <= 0.001;

                        rows.push({
                            id: `script:${scriptClip.id}:${sourceIndex}`,
                            label: `Script: ${name}`,
                            visible,
                            muted,
                            kind: "scriptSource",
                            scriptSourceIndex: sourceIndex,
                            thumbnailId: trackId || null,
                            mediaKind: "visual",
                        });
                    }
                } catch {}

                // Non-script clips that overlap this scene's timeline range.
                try {
                    const overlaps = (start: number, end: number) => start < sceneEnd && end > sceneStart;
                    for (const tr of comp.tracks ?? []) {
                        for (const c of (tr as any)?.clips ?? []) {
                            if (!c) continue;
                            if (String(c.id) === String(scriptClip.id)) continue;

                            const type = String((c as any).type ?? "");
                            const isAudio = type === "audio";
                            const isVisual =
                                type === "video" || type === "image" || type === "text" || type === "captions" || type === "placeholder";
                            if (!isVisual && !isAudio) continue;

                            const name = String((c as any).name ?? "");
                            const isLayoutManaged = name.startsWith("__libretto_layout__");
                            if (isLayoutManaged && type !== "placeholder") continue;

                            const start = Number((c as any).start?.seconds ?? 0);
                            const dur = Number((c as any).timelineDuration?.seconds ?? 0);
                            const end = start + Math.max(0, Number.isFinite(dur) ? dur : 0);
                            if (!overlaps(start, end)) continue;

                            const clipId = String((c as any).id);
                            const thumbnailId = (() => {
                                if (type === "image") return String(CompositionStateStore.clipIdToThumbnailId?.[clipId] ?? "");
                                if (type === "video") return String(CompositionStateStore.clipIdToAssetId?.[clipId] ?? "");
                                return "";
                            })();

	                            const visible = (() => {
	                                if (isAudio) return true;
	                                const ovAlpha = Number(
	                                    (c as any)?.sceneVisualOverridesByAspect?.[aspectPreset]?.[sceneKey]?.alpha ??
	                                        (c as any)?.sceneVisualOverrides?.[sceneKey]?.alpha
	                                );
	                                const baseAlpha = Number((c as any)?.alpha ?? 1);
	                                const alpha = Number.isFinite(ovAlpha) ? ovAlpha : Number.isFinite(baseAlpha) ? baseAlpha : 1;
	                                return !(Number.isFinite(alpha) && alpha <= 0.001);
	                            })();
                            const muted = (() => {
                                const eps = 1e-3;
                                if (isAudio) {
                                    const v = Number((c as any)?.volume ?? (c as any)?.gain ?? 1);
                                    return Number.isFinite(v) && v <= eps;
                                }
                                if (type === "video") {
                                    const g = Number((c as any)?.gain ?? 1);
                                    return Number.isFinite(g) && g <= eps;
                                }
                                return false;
                            })();

                            rows.push({
                                id: clipId,
                                label: String((c as any).name ?? (type ? `${type} clip` : "Clip")),
                                visible,
                                muted,
                                kind: "clip",
                                clipId,
                                thumbnailId: thumbnailId || null,
                                mediaKind: isAudio ? "audio" : "visual",
                            });
                        }
                    }
                } catch {}

                    const visualRowIds = rows.filter((r) => r.mediaKind !== "audio").map((r) => r.id);
                    const visualOrder = ScriptLayerStore.ensureLayoutVisualOrder(String(scriptClip.id), visualRowIds);
                    const indexById = new Map(visualOrder.map((id, i) => [id, i]));
                    const visualRows = rows.filter((r) => r.mediaKind !== "audio");
                    const audioRows = rows.filter((r) => r.mediaKind === "audio");
                    const sortedVisual = visualRows
                        .map((r, originalIndex) => ({
                            r,
                            originalIndex,
                            orderIndex: indexById.has(r.id) ? Number(indexById.get(r.id)) : Number.POSITIVE_INFINITY,
                        }))
                        .sort((a, b) => {
                            if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
                            return a.originalIndex - b.originalIndex;
                        })
                        .map((x) => x.r);

	                setLayers([...sortedVisual, ...audioRows]);
	            },
	            { fireImmediately: true }
	        );

	        return () => disposer();
	    }, [playheadSceneIndex]);

    useEffect(() => {
        const projectId = String(ProjectAssetsStore.projectId ?? "");
        if (!projectId) return;

        const pending = layers.filter((row) => {
            const thumbnailId = String(row.thumbnailId ?? "");
            if (!thumbnailId) return false;
            return !thumbnailUrlsByRowId[row.id];
        });
        if (pending.length === 0) return;

        let cancelled = false;
        void (async () => {
            const updates: Record<string, string> = {};
            for (const row of pending) {
                const thumbnailId = String(row.thumbnailId ?? "");
                if (!thumbnailId) continue;
                try {
                    const url = await GetThumbnailUrl({ thumbnailId, projectId });
                    if (cancelled) return;
                    if (url) updates[row.id] = url;
                } catch {}
            }
            if (cancelled) return;
            if (Object.keys(updates).length === 0) return;
            setThumbnailUrlsByRowId((prev) => ({ ...prev, ...updates }));
        })();

        return () => {
            cancelled = true;
        };
    }, [layers, GetThumbnailUrl, thumbnailUrlsByRowId]);

    const hasLayers = layers.length > 0;
    const visualLayers = layers.filter((l) => l.mediaKind !== "audio");
    const audioLayers = layers.filter((l) => l.mediaKind === "audio");
    const sortVisualLayersByOrder = (order: string[]) => {
        const indexById = new Map(order.map((id, i) => [id, i]));
        setLayers((prev) => {
            const visuals = prev.filter((r) => r.mediaKind !== "audio");
            const audios = prev.filter((r) => r.mediaKind === "audio");
            const sorted = visuals
                .map((r, originalIndex) => ({
                    r,
                    originalIndex,
                    orderIndex: indexById.has(r.id) ? Number(indexById.get(r.id)) : Number.POSITIVE_INFINITY,
                }))
                .sort((a, b) => {
                    if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
                    return a.originalIndex - b.originalIndex;
                })
                .map((x) => x.r);
            return [...sorted, ...audios];
        });
    };

    const reapplyCurrentSceneLayout = () => {
        const scriptClip: any = CompositionService.getScriptLayerClip();
        if (!scriptClip || playheadSceneIndex == null) return;
        try {
            const comp: any = CompositionService.composition;
            const cw = Number((ApplicationStore as any)?.canvasResolution?.[0] ?? comp?.settings?.width ?? comp?.width ?? 1920);
            const ch = Number((ApplicationStore as any)?.canvasResolution?.[1] ?? comp?.settings?.height ?? comp?.height ?? 1080);
            const ratio = ch > 0 ? cw / ch : 16 / 9;
            const aspectPreset = Math.abs(ratio - 1) < 0.06 ? "square" : ratio < 1 ? "vertical" : "horizontal";
            const layoutRef = scriptClip.getSceneLayout?.(playheadSceneIndex, aspectPreset) ?? null;
            if (!layoutRef) return;
            LayoutService.applySceneLayout(playheadSceneIndex, layoutRef);
        } catch {}
    };

    const handleReorderDrop = (fromId: string, toId: string) => {
        const scriptClip: any = CompositionService.getScriptLayerClip();
        if (!scriptClip) return;
        if (!fromId || !toId || fromId === toId) return;

        const storedOrder = ScriptLayerStore.getLayoutVisualOrder(String(scriptClip.id));
        const currentOrder = storedOrder.length > 0 ? storedOrder : visualLayers.map((l) => l.id);
        const fromIndex = currentOrder.indexOf(fromId);
        const toIndexInCurrent = currentOrder.indexOf(toId);

        const next = currentOrder.filter((id) => id !== fromId);
        const toIndex = next.indexOf(toId);
        if (toIndex >= 0) {
            const insertAfter = fromIndex >= 0 && toIndexInCurrent >= 0 && fromIndex < toIndexInCurrent;
            next.splice(toIndex + (insertAfter ? 1 : 0), 0, fromId);
        } else {
            next.push(fromId);
        }

        ScriptLayerStore.setLayoutVisualOrder(String(scriptClip.id), next);
        sortVisualLayersByOrder(next);
        reapplyCurrentSceneLayout();
    };

	    const toggleLayerVisibility = (row: LayerRow) => {
	        const scriptClip: any = CompositionService.getScriptLayerClip();
	        if (!scriptClip || playheadSceneIndex == null) return;

        let sceneKey = "__start__";
        try {
            const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
            const s =
                scenes.find((x: any) => Number(x?.index) === Number(playheadSceneIndex)) ??
                scenes[playheadSceneIndex];
            if (s) sceneKey = s?.startSplitId ? String(s.startSplitId) : "__start__";
        } catch {}

	        const nextAlpha = row.visible ? 0 : 1;
	        const hiddenByUser = nextAlpha <= 0.001 ? true : undefined;

	        if (row.kind === "scriptSource" && Number.isFinite(Number(row.scriptSourceIndex))) {
	            try {
	                scriptClip.setSceneVisualProperties?.(playheadSceneIndex, Number(row.scriptSourceIndex), { alpha: nextAlpha, hiddenByUser } as any);
	                CompositionService.notifyClipUpdated(scriptClip);
	                void CompositionService.composition?.recomputeFrameAtCurrentTimeAsync?.();
	                ScriptLayerStore.bumpVisualPropsVersion();
	            } catch {}
	            return;
	        }

	        if (row.kind === "clip" && row.clipId) {
	            const clip: any = CompositionService.getClipById(row.clipId);
	            if (!clip) return;
	            try {
	                CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { alpha: nextAlpha, hiddenByUser } as any);
	                CompositionService.notifyClipUpdated(clip);
	                void CompositionService.composition?.recomputeFrameAtCurrentTimeAsync?.();
	                ScriptLayerStore.bumpVisualPropsVersion();
	            } catch {}
	        }
	    };
    const toggleLayerMute = (row: LayerRow) => {
        const EPS = 1e-3;
        const scriptClip: any = CompositionService.getScriptLayerClip();
        if (!scriptClip || playheadSceneIndex == null) return;

        if (row.kind === "scriptSource" && Number.isFinite(Number(row.scriptSourceIndex))) {
            const sourceIndex = Number(row.scriptSourceIndex);
            const videoEntries: any[] = Array.isArray(scriptClip.getVideoEntries?.()) ? scriptClip.getVideoEntries() : [];
            const videoEntry = videoEntries?.[sourceIndex];
            const input = videoEntry?.input;
            if (!input) return;

            const cfgs: any[] = Array.isArray(scriptClip.getInputConfigs?.()) ? scriptClip.getInputConfigs() : [];
            const cfg = cfgs.find((c: any) => c?.input === input);
            const audioEntries: any[] = Array.isArray(scriptClip.getAudioEntries?.()) ? scriptClip.getAudioEntries() : [];
            const audioEntryIndex = audioEntries.findIndex((e: any) => e?.input === input);
            const audioEntry = audioEntryIndex >= 0 ? audioEntries[audioEntryIndex] : undefined;

            const baseGain = Number(audioEntry?.gain ?? cfg?.gain ?? 1);
            const currentMultiplier = Number(
                typeof scriptClip.getEffectiveAudioSourceGainMultiplier === "function"
                    ? scriptClip.getEffectiveAudioSourceGainMultiplier(playheadSceneIndex, audioEntryIndex >= 0 ? audioEntryIndex : sourceIndex)
                    : 1,
            );
            const effectiveGain = (Number.isFinite(baseGain) ? baseGain : 1) * (Number.isFinite(currentMultiplier) ? currentMultiplier : 1);
            const isMuted = Number.isFinite(effectiveGain) && effectiveGain <= EPS;

            if (!isMuted) {
                // Store both base gain + multiplier so we can restore them when unmuting.
                prevGainByRowIdRef.current[row.id] = Number.isFinite(baseGain) ? baseGain : 1;
                prevMultiplierByRowIdRef.current[row.id] = Number.isFinite(currentMultiplier) ? currentMultiplier : 1;
            }

            const restoreMultiplier = Number(prevMultiplierByRowIdRef.current[row.id] ?? 1);
            const nextMultiplier = isMuted ? (restoreMultiplier > EPS ? restoreMultiplier : 1) : 0;

            try {
                // If the base gain is 0, treat this as a global mute and restore base gain on unmute.
                if (isMuted && Number.isFinite(baseGain) && baseGain <= EPS) {
                    const restoreBase = Number(prevGainByRowIdRef.current[row.id] ?? 1);
                    const nextBase = restoreBase > EPS ? restoreBase : 1;
                    if (cfg) cfg.gain = nextBase;
                    if (audioEntry) audioEntry.gain = nextBase;
                    try {
                        scriptClip.gain = scriptClip.gain;
                    } catch {}
                    const targetAudioIndex = audioEntryIndex >= 0 ? audioEntryIndex : sourceIndex;
                    if (typeof scriptClip.setSceneAudioSourceGainMultiplier === "function") {
                        scriptClip.setSceneAudioSourceGainMultiplier(playheadSceneIndex, targetAudioIndex, null);
                    }
                } else if (typeof scriptClip.setSceneAudioSourceGainMultiplier === "function") {
                    const targetAudioIndex = audioEntryIndex >= 0 ? audioEntryIndex : sourceIndex;
                    if (Math.abs(nextMultiplier - 1) <= EPS) {
                        scriptClip.setSceneAudioSourceGainMultiplier(playheadSceneIndex, targetAudioIndex, null);
                    } else {
                        scriptClip.setSceneAudioSourceGainMultiplier(playheadSceneIndex, targetAudioIndex, nextMultiplier);
                    }
                }
            } catch {}

            try {
                CompositionService.notifyClipUpdated(scriptClip);
            } catch {}

            // Best-effort: muting affects transcript assembly and should repaint sidebar.
            try {
                ScriptLayerStore.bumpCutsVersion();
            } catch {}
            try {
                ScriptLayerStore.bumpVisualPropsVersion();
            } catch {}
            return;
        }

        if (row.kind === "clip" && row.clipId) {
            const clip: any = CompositionService.getClipById(row.clipId);
            if (!clip) return;
            if (!isAudioClip(clip) && !isVideoClip(clip)) return;

            const currentGain = Number((clip as any)?.volume ?? (clip as any)?.gain ?? 1);
            const isMuted = Number.isFinite(currentGain) && currentGain <= EPS;
            if (!isMuted) {
                prevGainByRowIdRef.current[row.id] = currentGain;
            }

            const restoreGain = Number(prevGainByRowIdRef.current[row.id] ?? 1);
            const nextGain = isMuted ? (restoreGain > EPS ? restoreGain : 1) : 0;

            try {
                if (isAudioClip(clip) && typeof clip.volume === "number") {
                    clip.volume = nextGain;
                } else {
                    clip.gain = nextGain;
                    try { CompositionService.composition?.onClipTimingChanged?.(); } catch {}
                }
            } catch {}

            try { CompositionService.notifyClipUpdated(clip); } catch {}
            try { if (clip.id) ScriptLayerStore.setClipGain(clip.id, nextGain); } catch {}
            try { ScriptLayerStore.bumpVisualPropsVersion(); } catch {}
        }
    };

    return (
        <PaperPanel title="Layers" showDivider={hasLayers}>
            {hasLayers && (
                <Box sx={{ px: 0.5, py: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
	                    {visualLayers.map((row) => (
	                        (() => {
	                            const isHidden = !row.visible;
	                            const isMuted = Boolean(row.muted);
	                            const thumbnailUrl = thumbnailUrlsByRowId[row.id];
	                            return (
	                        <Box
	                            key={row.id}
                                draggable
                                onDragStart={(e) => {
                                    draggingRowIdRef.current = row.id;
                                    setDraggingRowId(row.id);
                                    try {
                                        e.dataTransfer.effectAllowed = "move";
                                        e.dataTransfer.setData("text/plain", row.id);
                                    } catch {}
                                }}
                                onDragEnd={() => {
                                    draggingRowIdRef.current = null;
                                    setDraggingRowId(null);
                                    setDragOverRowId(null);
                                }}
	                                onDragOver={(e) => {
	                                    const currentDragging = draggingRowIdRef.current;
	                                    if (!currentDragging || currentDragging === row.id) return;
	                                    e.preventDefault();
	                                    setDragOverRowId(row.id);
                                }}
                                onDragLeave={() => {
                                    if (dragOverRowId === row.id) setDragOverRowId(null);
                                }}
                                onDrop={(e) => {
                                    const currentDragging = draggingRowIdRef.current;
                                    if (!currentDragging) return;
                                    e.preventDefault();
                                    handleReorderDrop(currentDragging, row.id);
                                    setDragOverRowId(null);
                                    setDraggingRowId(null);
                                    draggingRowIdRef.current = null;
                                }}
	                            sx={{
	                                display: "flex",
	                                alignItems: "center",
	                                gap: 1,
	                                px: 0.75,
                                py: 0.5,
                                borderRadius: "8px",
	                                background: "transparent",
                                    cursor: "grab",
                                    "&:active": { cursor: "grabbing" },
	                                ...(dragOverRowId === row.id && draggingRowId && draggingRowId !== row.id
	                                        ? { outline: "2px solid #2B6BFD", outlineOffset: "-2px" }
	                                        : {}),
	                                "&:hover": {
	                                    background: "var(--app-bg-subtle)",
	                                },
	                                "&:hover .layers__actions": {
                                    opacity: 1,
                                    pointerEvents: "auto",
                                },
	                            }}
	                        >
	                            <Box
	                                sx={{
	                                    display: "flex",
	                                    alignItems: "center",
	                                    gap: 0.75,
                                    minWidth: 0,
                                    flex: 1,
                                    filter: isHidden ? "blur(0.6px)" : "none",
                                    opacity: isHidden ? 0.7 : 1,
                                }}
                            >
	                                <Box
	                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: "6px",
                                        overflow: "hidden",
                                        flexShrink: 0,
                                        border: "1px solid var(--app-border-subtle)",
                                        background: "var(--app-bg-subtle)",
                                    }}
                                >
	                                    {thumbnailUrl && (
	                                        <Box
	                                            component="img"
	                                            src={thumbnailUrl}
                                                draggable={false}
	                                            alt=""
	                                            sx={{
	                                                width: "100%",
	                                                height: "100%",
                                                objectFit: "cover",
                                                display: "block",
                                            }}
                                        />
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "var(--app-text-muted)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {row.label}
                                </Typography>
                            </Box>
                            <Box
                                className="layers__actions"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                    opacity: 0,
                                    pointerEvents: "none",
                                    transition: "opacity 0.15s ease",
                                }}
                            >
                                <Tooltip title={isMuted ? "Unmute layer" : "Mute layer"} placement="top">
                                    <IconButton
                                        size="small"
                                        onClick={() => toggleLayerMute(row)}
                                        sx={{ p: 0.25, color: "var(--app-text-subtle)", "&:hover": { color: "var(--app-text-muted)" } }}
                                    >
                                        {isMuted ? (
                                            <VolumeOffOutlinedIcon fontSize="small" />
                                        ) : (
                                            <VolumeUpOutlinedIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={row.visible ? "Hide layer" : "Unhide layer"} placement="top">
                                    <IconButton
                                        size="small"
                                        onClick={() => toggleLayerVisibility(row)}
                                        sx={{ p: 0.25, color: "var(--app-text-subtle)", "&:hover": { color: "var(--app-text-muted)" } }}
                                    >
                                        {row.visible ? (
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        ) : (
                                            <VisibilityOffOutlinedIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                            );
                        })()
                    ))}
                    {audioLayers.length > 0 && visualLayers.length > 0 && (
                        <Divider sx={{ my: 0.75, borderColor: "var(--app-border-subtle)" }} />
                    )}
                    {audioLayers.map((row) => (
                        (() => {
                            const isMuted = Boolean(row.muted);
                            return (
                                <Box
                                    key={row.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 0.75,
                                        py: 0.5,
                                        borderRadius: "8px",
                                        background: "transparent",
                                        "&:hover": {
                                            background: "var(--app-bg-subtle)",
                                        },
                                        "&:hover .layers__actions": {
                                            opacity: 1,
                                            pointerEvents: "auto",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            minWidth: 0,
                                            flex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                flexShrink: 0,
                                                border: "1px solid var(--app-border-subtle)",
                                                background: "var(--app-bg-subtle)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <AudioWaveformIcon sx={{ fontSize: 14, color: "var(--app-text-muted)" }} />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "var(--app-text-muted)",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {row.label}
                                        </Typography>
                                    </Box>
                                    <Box
                                        className="layers__actions"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.25,
                                            opacity: 0,
                                            pointerEvents: "none",
                                            transition: "opacity 0.15s ease",
                                        }}
                                    >
                                        <Tooltip title={isMuted ? "Unmute layer" : "Mute layer"} placement="top">
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleLayerMute(row)}
                                                sx={{ p: 0.25, color: "var(--app-text-subtle)", "&:hover": { color: "var(--app-text-muted)" } }}
                                            >
                                                {isMuted ? (
                                                    <VolumeOffOutlinedIcon fontSize="small" />
                                                ) : (
                                                    <VolumeUpOutlinedIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })()
                    ))}
                </Box>
            )}
        </PaperPanel>
    );
};

// --- Helpers ---
function isAudioClip(obj: any): obj is AudioClip {
    return obj instanceof AudioClip;
}

function isVideoClip(obj: any): obj is VideoClip {
    return obj instanceof VideoClip;
}

function isImageClip(obj: any): obj is ImageClip {
    return obj instanceof ImageClip;
}

function isScriptClip(obj: any): obj is ScriptLayerClip {
    return obj instanceof ScriptLayerClip;
}

const normalizeState = (value: any) => String(value ?? '').toLowerCase();

const isSilentState = (value: any) => {
    const state = normalizeState(value);
    return state === 'silent' || state === 'not_needed';
};

function isTrackSilent(track: any): boolean {
    if (!track) return false;
    if (isSilentState(track?.transcriptionState)) return true;
    if (isSilentState(track?.enhancedTrackState)) return true;
    return false;
}

const titleTextStyle = {
    color: 'var(--app-text-primary)',
    paddingLeft: '8px',            // px-2
    paddingRight: '8px',
    flex: 1,                       // flex-1
    overflow: 'hidden',            // overflow-hidden
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',              // body-2-medium (typically ~14px)
    fontWeight: 500,               // medium
    lineHeight: '20px',            // matches Tailwind body line-height
    fontFamily: 'Inter, sans-serif',
    userSelect: 'none',
};

const ChangeLayoutPackButton = ({disabled, onClick}) => {
    const boxStyle = {
        display: "flex",
        flexDirection: "row",
        height: "40px",
        width: "280px",
        padding: "10px 20px",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        borderRadius: "6px",
        backgroundColor: disabled ? "#C0C0C0" : "#1a1a1a",
        border: "1px solid #AEAEAE",
        cursor: disabled ? "not-allowed" : "pointer",
        // pointerEvents: newProjectDisabled ? "none" : "auto",
        "&:hover": disabled ? {} : {
            border: "1px solid #6D6D6D",
        },
        "&:focus": disabled ? {} : {
            border: "1px solid #6D6D6D",
        },
    };

    const textStyle = {
        fontFamily: "Inter",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "20px",
        letterSpacing: "0.14px",
    }

    const buttonTextStyle = {
        ...textStyle,
        color: disabled ? "#6e6e6e" : "white",
    };

    const iconBoxStyle = {
        paddingTop: "6px",
    };

    return (
            <Box
                sx={boxStyle}
                onClick={disabled ? () => {} : onClick}
            >
                <Box style={buttonTextStyle}>
                    Change layout pack
                </Box>
            </Box>
    );
}

const DeferredVolumeControl: React.FC<{
    committedPct: number;
    onCommit: (pct: number) => void;
    onToggleMute: () => void;
    onDragStart?: () => void;
}> = React.memo(({ committedPct, onCommit, onToggleMute, onDragStart }) => {
    const [uiPct, setUiPct] = useState<number>(normalizeVolumePct(committedPct));
    const [uiPctInput, setUiPctInput] = useState<string>(String(normalizeVolumePct(committedPct)));
    const draggingRef = useRef(false);
    const editingInputRef = useRef(false);

    const syncUiPct = (next: number, preserveInputWhileEditing: boolean = true) => {
        const normalized = normalizeVolumePct(next);
        setUiPct(normalized);
        if (!preserveInputWhileEditing || !editingInputRef.current) {
            setUiPctInput(String(normalized));
        }
        return normalized;
    };

    useEffect(() => {
        if (draggingRef.current || editingInputRef.current) return;
        const normalized = normalizeVolumePct(committedPct);
        setUiPct(normalized);
        setUiPctInput(String(normalized));
    }, [committedPct]);

    const handleChange = (_: any, value: number | number[]) => {
        if (!draggingRef.current) {
            try { onDragStart?.(); } catch {}
        }
        draggingRef.current = true;
        const next = Array.isArray(value) ? value[0] : value;
        syncUiPct(next);
    };

    const handleCommit = (_: any, value: number | number[]) => {
        const committed = Array.isArray(value) ? value[0] : value;
        const pct = normalizeVolumePct(committed);
        draggingRef.current = false;
        syncUiPct(pct, false);
        onCommit(pct);
    };

    const commitInput = () => {
        const trimmed = uiPctInput.trim();
        const parsed = trimmed.length ? Number(trimmed) : Number.NaN;
        const pct = Number.isFinite(parsed) ? normalizeVolumePct(parsed) : uiPct;
        editingInputRef.current = false;
        try { onDragStart?.(); } catch {}
        syncUiPct(pct, false);
        onCommit(pct);
    };

    const cancelInput = () => {
        editingInputRef.current = false;
        syncUiPct(committedPct, false);
    };

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 1}}>
            <IconButton
                size="small"
                onClick={onToggleMute}
                sx={{ p: 0.25, color: "text.secondary" }}
            >
                {uiPct <= 0 ? (
                    <VolumeOffRoundedIcon sx={{ fontSize: 18 }} />
                ) : (
                    <VolumeUpRoundedIcon sx={{ fontSize: 18 }} />
                )}
            </IconButton>
            <Typography sx={{fontSize: 12, color: 'text.secondary', width: 52}}>Volume</Typography>
            <Slider
                size="small"
                value={uiPct}
                onChange={handleChange}
                onChangeCommitted={handleCommit}
                min={0}
                max={400}
                step={1}
                sx={{flex: 1, color: 'text.primary'}}
            />
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25, width: 70}}>
                <TextField
                    size="small"
                    value={uiPctInput}
                    onFocus={() => {
                        editingInputRef.current = true;
                    }}
                    onChange={(e) => {
                        const next = e.target.value.replace(/[^\d]/g, "");
                        setUiPctInput(next);
                        if (!next.length) return;
                        const parsed = Number(next);
                        if (Number.isFinite(parsed)) {
                            syncUiPct(parsed);
                        }
                    }}
                    onBlur={() => {
                        if (!editingInputRef.current) return;
                        commitInput();
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commitInput();
                            return;
                        }
                        if (e.key === "Escape") {
                            e.preventDefault();
                            cancelInput();
                        }
                    }}
                    inputProps={{
                        inputMode: "numeric",
                        "aria-label": "Volume percent",
                        style: { textAlign: "right", fontSize: 12, padding: "4px 6px" },
                    }}
                    sx={{ width: 50, "& .MuiInputBase-input": { textAlign: "right" } }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>%</Typography>
            </Box>
        </Box>
    );
});

// --- Main component ---
const LayoutOption = ({
    name,
    selected,
    onClick,
    previewUrl,
    posterUrl,
    previewObjectFit = 'cover',
    moreButton,
    dataTour,
}: {
    name: string;
    selected: boolean;
    onClick: () => void;
    previewUrl?: string;
    posterUrl?: string;
    previewObjectFit?: 'cover' | 'contain';
    moreButton?: { label?: string; onClick: (event: React.MouseEvent<HTMLElement>) => void };
    dataTour?: string;
}) => {

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const handleEnter = () => {
        if (!previewUrl) return;
        const v = videoRef.current;
        if (v) {
            try {
                v.currentTime = 0;
                // On some browsers play() returns a promise
                const p = v.play();
                if (p && typeof p.catch === 'function') p.catch(() => {});
            } catch {}
        }
    };
    const handleLeave = () => {
        if (!previewUrl) return;
        const v = videoRef.current;
        if (v) {
            try { v.pause(); } catch {}
        }
    };
    return (
        <Box
            data-tour={dataTour}
            onClick={onClick}
            onMouseEnter={previewUrl ? handleEnter : undefined}
            onMouseLeave={previewUrl ? handleLeave : undefined}
            sx={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 132,
                height: 114,
                borderRadius: '10px',
                border: selected ? '2px solid #2B6BFD' : '1px solid var(--app-border-subtle)',
                background: 'var(--app-bg-surface)',
                boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                userSelect: 'none',
                transition: 'all 0.15s',
                '&:hover': {boxShadow: '0 3px 10px rgba(0,0,0,0.12)'},
                '&:hover .layoutMoreButton': { display: 'block' },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "125px",
                    height: "70px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    mb: 0.5,
                    background: previewUrl ? "#000" : "var(--app-bg-subtle)",
                }}
            >
                {previewUrl ? (
                    <video
                        ref={videoRef}
                        src={previewUrl}
                        poster={posterUrl}
                        muted
                        playsInline
                        preload="metadata"
                        controls={false}
                        style={{ width: "100%", height: "100%", objectFit: previewObjectFit, display: "block" }}
                    />
                ) : posterUrl ? (
                    <img
                        src={posterUrl}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: previewObjectFit, display: "block" }}
                    />
                ) : null}
                {moreButton && (
                    <Box
                        className="layoutMoreButton"
                        onClick={(e) => {
                            e.stopPropagation();
                            moreButton.onClick(e);
                        }}
                        sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: "10px",
                            fontSize: 11,
                            lineHeight: "16px",
                            background: "rgba(0,0,0,0.65)",
                            color: "#fff",
                            display: "none",
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { background: "rgba(0,0,0,0.8)" },
                        }}
                    >
                        {moreButton.label ?? "More"}
                    </Box>
                )}
            </Box>
            <Typography sx={{fontSize: 11, color: 'var(--app-text-muted)', fontWeight: "medium", opacity: 0.9}}>{name}</Typography>
        </Box>
    );
};

/**
 * Get visual element properties based on selectedVisualElement.
 * For ScriptLayerClip sources: reads from videoEntries[sourceIndex].visual
 * For regular clips (sourceIndex undefined): reads from clip properties
 */
function getVisualProps(clip: any, sourceIndex: number | undefined): {
    x: number; y: number; width: number; height: number; alpha: number;
} | null {
    if (!clip) return null;

    const cw = Number(CompositionStateStore.display?.width ?? 1920);
    const ch = Number(CompositionStateStore.display?.height ?? 1080);

    const toFiniteNumber = (value: unknown, fallback: number, opts?: { percentOf?: number }) => {
        if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
        if (typeof value === "string") {
            const s = value.trim();
            if (opts?.percentOf != null && s.endsWith("%")) {
                const pct = Number(s.slice(0, -1));
                if (Number.isFinite(pct)) return (pct / 100) * opts.percentOf;
            }
        }
        const n = Number(value);
        return Number.isFinite(n) ? n : fallback;
    };
    
    if (isScriptClip(clip) && sourceIndex !== undefined) {
        const videoEntries = clip.getVideoEntries?.() || [];
        const entry = videoEntries[sourceIndex];
        if (entry?.visual) {
            return {
                x: toFiniteNumber(entry.visual.x, 0),
                y: toFiniteNumber(entry.visual.y, 0),
                width: toFiniteNumber(entry.visual.width, 0),
                height: toFiniteNumber(entry.visual.height, 0),
                alpha: toFiniteNumber(entry.visual.alpha, 1),
            };
        }
        return null;
    }
    
    // Regular clip
    const pos = clip.position || { x: 0, y: 0 };
    return {
        x: toFiniteNumber(pos.x ?? clip.x, 0, { percentOf: cw }),
        y: toFiniteNumber(pos.y ?? clip.y, 0, { percentOf: ch }),
        width: toFiniteNumber(clip.width, 0, { percentOf: cw }),
        height: toFiniteNumber(clip.height, 0, { percentOf: ch }),
        alpha: toFiniteNumber(clip.alpha, 1),
    };
}

const PropertiesSidebarInner: React.FC = observer(() => {
    const { GetEnhancedAudioUrl, GetTrackUrl, TriggerEnhance, ResolveAssetUrl } = useAssetStore();
    const [selectedClipId, setSelectedClipId] = useState<string | null>(ApplicationStore.selectedClipId);
    const [selectedVisualElement, setSelectedVisualElement] = useState<{ clipId: string; sourceIndex?: number } | null>(
        ApplicationStore.selectedVisualElement
    );
    const [selectedSceneIndex, setSelectedSceneIndex] = useState<number | null>(ApplicationStore.selectedSceneIndex);
    const [sceneEditScope, setSceneEditScope] = useState<"scene" | "all">(ApplicationStore.sceneEditScope);
    const [sceneLayout, setSceneLayout] = useState<string | null>(null);
    const [activeLayoutPackId, setActiveLayoutPackId] = useState<string>(() => {
        const stored = loadStoredLayoutPackId(ProjectAssetsStore.projectId);
        return stored ?? DEFAULT_LAYOUT_PACK_ID;
    });
    const [layoutOpen, setLayoutOpen] = useState<boolean>(false);
    const [layoutAnchorEl, setLayoutAnchorEl] = useState<HTMLElement | null>(null);
    const layoutPickerButtonRef = useRef<HTMLDivElement | null>(null);
    const [multicamMoreOpen, setMulticamMoreOpen] = useState(false);
    const [multicamMoreAnchorEl, setMulticamMoreAnchorEl] = useState<HTMLElement | null>(null);
    const [multicamVariantKeyByPreset, setMulticamVariantKeyByPreset] = useState<Record<CanvasAspectPreset, string>>(() => ({
        horizontal: String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.horizontal ?? "two"),
        vertical: String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.vertical ?? "two"),
        square: String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.square ?? "two"),
    }));
    const [playheadSceneIndex, setPlayheadSceneIndex] = useState<number | null>(null);
    const [sceneBackgroundColor, setSceneBackgroundColor] = useState<string>(() => {
        const c = String((CompositionStateStore.display as any)?.backgroundColor ?? "#000000");
        return /^#[0-9a-fA-F]{6}$/.test(c) ? c : "#000000";
    });
    const [canvasAspectPreset, setCanvasAspectPreset] = useState<CanvasAspectPreset>(() =>
        getCanvasAspectPreset(CompositionStateStore.display.width, CompositionStateStore.display.height)
    );
    const [sceneTransitions, setSceneTransitions] = useState<{ in: TransitionEdgeState | null; out: TransitionEdgeState | null }>(
        {
            in: null,
            out: null,
        }
    );
    const [transitionMenuOpen, setTransitionMenuOpen] = useState(false);
    const [transitionMenuAnchorEl, setTransitionMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [transitionMenuHover, setTransitionMenuHover] = useState<"in" | "out" | null>(null);
    const [transitionSubmenuHover, setTransitionSubmenuHover] = useState(false);
    const transitionInRef = useRef<HTMLDivElement | null>(null);
    const transitionOutRef = useRef<HTMLDivElement | null>(null);
    const [transitionControlsAnchorEl, setTransitionControlsAnchorEl] = useState<HTMLElement | null>(null);
    const [transitionControlsEdge, setTransitionControlsEdge] = useState<"in" | "out" | null>(null);
    // Store original audio URLs for reverting when Studio Sound is toggled off
    const originalAudioUrlsRef = useRef<Map<string, string>>(new Map());

    useEffect(() => {
        const disposer = reaction(
            () => ({
                clipId: ApplicationStore.selectedClipId,
                visualElement: ApplicationStore.selectedVisualElement,
                sceneIndex: ApplicationStore.selectedSceneIndex,
                scope: ApplicationStore.sceneEditScope,
            }),
            ({ clipId, visualElement, sceneIndex, scope }) => {
                setSelectedClipId(clipId);
                setSelectedVisualElement(visualElement);
                setSelectedSceneIndex(sceneIndex);
                setSceneEditScope(scope);
            },
            { fireImmediately: true }
        );

        return () => {
            disposer();
        };
    }, []);

    // Persist selected layout pack per project (localStorage for now).
    useEffect(() => {
        const disposer = reaction(
            () => ProjectAssetsStore.projectId,
            (projectId) => {
                const stored = loadStoredLayoutPackId(projectId);
                if (stored && stored !== activeLayoutPackId) {
                    setActiveLayoutPackId(stored);
                }
            },
            { fireImmediately: true }
        );
        return () => disposer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!activeLayoutPackId) return;
        persistLayoutPackId(ProjectAssetsStore.projectId, activeLayoutPackId);
    }, [activeLayoutPackId]);

    // Once packs have loaded from Firestore, clamp any unknown stored pack id to the default.
    useEffect(() => {
        if (!LayoutPacksStore.hydrated) return;
        if (!activeLayoutPackId) {
            setActiveLayoutPackId(DEFAULT_LAYOUT_PACK_ID);
            return;
        }
        if (!LayoutPacksStore.hasPack(activeLayoutPackId)) {
            setActiveLayoutPackId(DEFAULT_LAYOUT_PACK_ID);
        }
    }, [LayoutPacksStore.hydrated, LayoutPacksStore.packs.length, activeLayoutPackId]);

    // Update layout previews based on the composition display aspect ratio.
    useEffect(() => {
        const disposer = reaction(
            () => ({
                width: CompositionStateStore.display.width,
                height: CompositionStateStore.display.height,
            }),
            ({ width, height }) => {
                setCanvasAspectPreset(getCanvasAspectPreset(width, height));
            },
            { fireImmediately: true }
        );

        return () => disposer();
    }, []);

    // Track playhead position to determine which scene we're in (used when no explicit scene is selected).
    useEffect(() => {
        const disposer = reaction(
            () => ({
                currentTime: CompositionStateStore.currentTime,
                scenesVersion: ScriptLayerStore.scenesVersion,
            }),
            ({ currentTime }) => {
                const scriptClip = CompositionService.getScriptLayerClip();
                if (!scriptClip) {
                    setPlayheadSceneIndex(null);
                    return;
                }

                // Prefer segment-based mapping so cuts/gaps don't desync scene detection.
                // Scene boundaries live in clip-local/source time (split.timeSec), while currentTime is in
                // timeline time. Map timeline -> source, then find the scene.
                let inferredSceneIndex: number | null = null;
                try {
                    const clipStartAbs = Number((scriptClip as any)?.start?.seconds ?? 0);
                    const relTime = Number.isFinite(currentTime) ? Number(currentTime) - clipStartAbs : NaN;
                    const srcTime = Number.isFinite(relTime) ? ScriptLayerStore.mapTimelineTimeToSourceTime(Math.max(0, relTime)) : null;
                    if (srcTime != null && Number.isFinite(srcTime) && typeof (scriptClip as any).findSceneIndexForTime === "function") {
                        const idx = (scriptClip as any).findSceneIndexForTime(srcTime);
                        inferredSceneIndex = Number.isFinite(idx) && idx >= 0 ? idx : null;
                    }
                } catch {}

                if (inferredSceneIndex != null) {
                    setPlayheadSceneIndex(inferredSceneIndex);
                    return;
                }

                if (typeof (scriptClip as any).getScenes !== "function") {
                    setPlayheadSceneIndex(null);
                    return;
                }

                const scenes = (scriptClip as any).getScenes();
                if (!scenes || scenes.length === 0) {
                    setPlayheadSceneIndex(null);
                    return;
                }

                // Find which scene contains the current playhead
                let sceneAtPlayhead = scenes.find((scene: any) =>
                    currentTime >= scene.timelineStartSec && currentTime < scene.timelineEndSec
                );

                if (!sceneAtPlayhead) {
                    const first = scenes[0];
                    const last = scenes[scenes.length - 1];
                    const firstStart = Number(first?.timelineStartSec ?? 0);
                    const lastEnd = Number(last?.timelineEndSec ?? 0);
                    if (Number.isFinite(firstStart) && currentTime < firstStart) {
                        sceneAtPlayhead = first;
                    } else if (Number.isFinite(lastEnd) && currentTime >= lastEnd) {
                        sceneAtPlayhead = last;
                    }
                }

                const newSceneIndex =
                    sceneAtPlayhead && Number.isFinite(Number((sceneAtPlayhead as any)?.index))
                        ? Number((sceneAtPlayhead as any).index)
                        : null;
                setPlayheadSceneIndex(newSceneIndex);
            },
            { fireImmediately: true }
        );

        return () => disposer();
    }, []);

    // Keep Layout + Background panels in sync with either:
    // - the explicitly selected scene (Scene bar), or
    // - the playhead scene when no explicit selection exists.
    useEffect(() => {
        const scriptClip: any = CompositionService.getScriptLayerClip();
        const globalBg = String((CompositionStateStore.display as any)?.backgroundColor ?? "#000000");

        const effectiveSceneIndex =
            typeof selectedSceneIndex === "number" && Number.isFinite(selectedSceneIndex)
                ? selectedSceneIndex
                : playheadSceneIndex;

        if (!scriptClip || effectiveSceneIndex == null) {
            setSceneLayout(null);
            setSceneBackgroundColor(/^#[0-9a-fA-F]{6}$/.test(globalBg) ? globalBg : "#000000");
            return;
        }

        try {
            const layout = scriptClip.getSceneLayout?.(effectiveSceneIndex) ?? null;
            setSceneLayout(layout ?? null);

            const parsed = parseLayoutRef(layout);
            if (parsed?.packId) setActiveLayoutPackId(parsed.packId);

            // Keep Multicam preview selection in sync with the stored scene layout (including variants).
            if (getLayoutKind(layout) === "multicam") {
                const pack =
                    (parsed && LayoutPacksStore.getPack(parsed.packId)) ??
                    LayoutPacksStore.getPack(activeLayoutPackId) ??
                    BUILTIN_LAYOUT_PACK;
                const candidates = (pack.multicamVariantsByPreset?.[canvasAspectPreset] ?? []) as MulticamVariant[];
                const defaultKey =
                    String(pack.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "").trim() ||
                    String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "two");
                const inferred = parseMulticamVariantKey(layout) ?? inferMulticamVariantKey(scriptClip, canvasAspectPreset, defaultKey);
                const valid = candidates.some((v) => v.key === inferred) ? inferred : defaultKey;
                setMulticamVariantKeyByPreset((prev) => ({ ...prev, [canvasAspectPreset]: valid }));
            }
        } catch {}

        // Background override
        try {
            let nextBg = globalBg;
            const props = scriptClip.getSceneProperties?.(effectiveSceneIndex) as any;
            if (typeof props?.backgroundColor === "string" && props.backgroundColor.trim()) {
                nextBg = String(props.backgroundColor).trim();
            }
            setSceneBackgroundColor(/^#[0-9a-fA-F]{6}$/.test(nextBg) ? nextBg : /^#[0-9a-fA-F]{6}$/.test(globalBg) ? globalBg : "#000000");
        } catch {
            setSceneBackgroundColor(/^#[0-9a-fA-F]{6}$/.test(globalBg) ? globalBg : "#000000");
        }
    }, [selectedSceneIndex, playheadSceneIndex, canvasAspectPreset]);

    // Handle "Change Layout" flow from scene context menus: open the layout picker in the Properties sidebar.
    useEffect(() => {
        const disposer = reaction(
            () => ({
                pendingSceneIndex: ApplicationStore.pendingSceneLayoutPickerContext?.sceneIndex ?? null,
                clipId: ApplicationStore.selectedClipId,
            }),
            ({ pendingSceneIndex, clipId }) => {
                if (clipId != null) return;
                if (pendingSceneIndex == null) return;
                const anchor = layoutPickerButtonRef.current;
                if (!anchor) return;
                setLayoutAnchorEl(anchor);
                setMulticamMoreOpen(false);
                setLayoutOpen(true);
                ApplicationStore.consumePendingSceneLayoutPickerContext();
            },
            { fireImmediately: true }
        );
        return () => disposer();
    }, []);

    const engineTransitionToUi = (transition?: EngineTransitionConfig): TransitionEdgeState | null => {
        const video = (transition as any)?.video;
        const type = String(video?.type ?? "");
        const durationMs = Number(video?.durationMs ?? 0);
        const durationSec = Number.isFinite(durationMs) ? Math.max(0, durationMs / 1000) : 0;
        const duration = durationSec.toFixed(1);

        if (type === "fade") {
            if (durationSec <= 0) return null;
            return { type: "fade", duration, easing: DEFAULT_SMART_EASING, addFades: false };
        }

        if (type === "smart" || type === "dissolve") {
            if (durationSec <= 0) return null;
            return {
                type: "smart",
                duration,
                easing: String(video?.easing ?? DEFAULT_SMART_EASING),
                addFades: Boolean(video?.addFades ?? false),
            };
        }

        return null;
    };

    const uiTransitionToEngine = (state: TransitionEdgeState): EngineTransitionConfig => {
        const durationSec = Number(state.duration ?? DEFAULT_TRANSITION_DURATION);
        const durationMs = Number.isFinite(durationSec) ? Math.max(0, durationSec) * 1000 : 0;

        if (state.type === "fade") {
            return {
                video: { type: "fade", durationMs },
            };
        }

        return {
            video: {
                type: "smart",
                durationMs,
                easing: String(state.easing ?? DEFAULT_SMART_EASING),
                addFades: Boolean(state.addFades ?? false),
            },
        };
    };

    const applySceneTransition = (edge: "in" | "out", next: TransitionEdgeState | null) => {
        const sceneIndex = playheadSceneIndex;
        if (sceneIndex == null) return;

        const scriptClip: any = CompositionService.getScriptLayerClip();
        if (!scriptClip) return;
        const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];

        const setIn = (idx: number, value: EngineTransitionConfig | undefined) => {
            try { scriptClip.setSceneInTransition?.(idx, value); } catch {}
        };
        const setOut = (idx: number, value: EngineTransitionConfig | undefined) => {
            try { scriptClip.setSceneOutTransition?.(idx, value); } catch {}
        };

        const engineValue = next ? uiTransitionToEngine(next) : undefined;

        if (edge === "in") {
            setIn(sceneIndex, engineValue);
            if (sceneIndex > 0) setOut(sceneIndex - 1, engineValue);
        } else {
            setOut(sceneIndex, engineValue);
            if (sceneIndex + 1 < (scenes?.length ?? 0)) setIn(sceneIndex + 1, engineValue);
        }

        // Persist sceneProperties changes (transitions) and refresh schedule.
        try { scriptClip.track?.composition?.onClipTimingChanged?.(); } catch {}
        try { scriptClip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: scriptClip }); } catch {}
        try { ScriptLayerStore.bumpCutsVersion(); } catch {}
    };

    // Keep Transitions panel in sync with the engine scene transitions at the playhead.
    useEffect(() => {
        const scriptClip: any = CompositionService.getScriptLayerClip();
        if (!scriptClip || playheadSceneIndex == null) {
            setSceneTransitions({ in: null, out: null });
            return;
        }

        try {
            const inT = scriptClip.getSceneInTransition?.(playheadSceneIndex) as EngineTransitionConfig | undefined;
            const outT = scriptClip.getSceneOutTransition?.(playheadSceneIndex) as EngineTransitionConfig | undefined;
            setSceneTransitions({
                in: engineTransitionToUi(inT),
                out: engineTransitionToUi(outT),
            });
        } catch {
            setSceneTransitions({ in: null, out: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playheadSceneIndex]);

    const clip: any = selectedClipId ? CompositionService.getClipById(selectedClipId) : null;
    const sourceIndex = selectedVisualElement?.sourceIndex;

    const findTrackRecord = (trackId?: string) => {
        const id = String(trackId ?? '');
        if (!id) return undefined;
        return ProjectAssetsStore.projectTracks.find((t: any) => {
            const tid = String(t?.id ?? '');
            const tTrackId = String(t?.trackId ?? '');
            return tid === id || tTrackId === id;
        });
    };

    const inferMulticamVariantKey = (scriptClip: any, preset: CanvasAspectPreset, defaultKey: string): string => {
        try {
            const sources: any[] = typeof scriptClip?.getVideoSources === "function" ? scriptClip.getVideoSources() : [];
            const count = Array.isArray(sources) ? sources.length : 0;
            if (count <= 0) return defaultKey;

            const cfgs: any[] = typeof scriptClip?.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
            const entries: any[] = typeof scriptClip?.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];

            const isScreenSource = (srcIdx: number): boolean => {
                try {
                    const entry = entries?.[srcIdx];
                    if (!entry?.input) return false;
                    const cfg = cfgs.find((c: any) => c?.input === entry.input);
                    const trackId = String(cfg?.assetId ?? "");
                    const tr = findTrackRecord(trackId);
                    if (typeof tr?.isScreenRecording === "boolean") return Boolean(tr.isScreenRecording);
                    const title = String(tr?.title ?? cfg?.name ?? "");
                    return /screen/i.test(title);
                } catch {}
                return false;
            };

            const screenIdx = sources.findIndex((_: any, idx: number) => isScreenSource(idx));
            const hasScreen = screenIdx >= 0;

            if (preset === "horizontal") {
                if (hasScreen && count >= 3) {
                    const cams = Math.max(0, count - 1);
                    const base = cams <= 2 ? "two" : cams === 3 ? "three" : cams === 4 ? "four" : "four";
                    return `${base}_screen`;
                }
                if (count <= 2) return "two";
                if (count === 3) return "three";
                if (count === 4) return "four";
                if (count === 5) return "five";
                return "six";
            }

            // vertical/square: cap at 4
            if (count <= 2) return "two";
            if (count === 3) return "three";
            return "four";
        } catch {}

        return defaultKey;
    };

	    // Derived flags
	    const hasClip = !!clip;
	    const isMedia = hasClip && (isAudioClip(clip) || isVideoClip(clip) || isScriptClip(clip));
	    const hasAudioStream = hasClip && (isAudioClip(clip) || (isVideoClip(clip) && !!clip.hasAudioStream) || isScriptClip(clip));
    const supportsClipAnimations = hasClip && (isVideoClip(clip) || isImageClip(clip) || isScriptClip(clip));
    // Has visual element if selectedVisualElement is set
    const hasVisualElement = !!selectedVisualElement;
    // Check if editing scene-specific properties
    const isEditingScene = isScriptClip(clip) && selectedSceneIndex !== null;
    const spansMultipleScenes = hasClip ? CompositionService.clipSpansMultipleScenes(clip) : false;
    const shouldShowSceneScopeToggle = hasVisualElement && spansMultipleScenes;

	    /* Placeholder replacement (deprecated): replaced by placeholder context-menu “Add media”.
	    const openReplaceDialog = () => {
	        setReplaceDialogOpen(true);
	        setReplaceKind("video");
	        setReplaceSelectionId("");
	    };

    const closeReplaceDialog = () => {
        setReplaceDialogOpen(false);
        setReplaceSelectionId("");
    };

    const getPlaceholderTiming = (ph: any): { startSec: number; stopSec: number } | null => {
        if (!ph) return null;
        const startSec = Number(ph?.start?.seconds ?? 0);
        const stopSec = Number(ph?.stop?.seconds ?? startSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(stopSec) || stopSec <= startSec) return null;
        return { startSec, stopSec };
    };

    const getPlaceholderLayerId = (ph: any): string | null => {
        const layerId = String(ph?.track?.id ?? "");
        return layerId || null;
    };

    const replacePlaceholderWithAsset = async () => {
        if (!isPlaceholder || !clip) return;
        const layerId = getPlaceholderLayerId(clip);
        const timing = getPlaceholderTiming(clip);
        if (!layerId || !timing) return;
        const assetId = String(replaceSelectionId || "");
        if (!assetId) return;

        try {
            const compAny: any = CompositionService.composition as any;
            const placeholderTrackIndex =
                compAny && Array.isArray(compAny.tracks)
                    ? compAny.tracks.findIndex((t: any) => String(t?.id ?? "") === String(layerId))
                    : -1;

            const placeholderDur = Math.max(0, Number(timing.stopSec) - Number(timing.startSec));
            if (!(placeholderDur > 0)) return;

            const phPos = (clip as any)?.position ?? { x: 0, y: 0 };
            const phX = Number(phPos?.x ?? 0);
            const phY = Number(phPos?.y ?? 0);
            const phW = Number((clip as any)?.width ?? 0);
            const phH = Number((clip as any)?.height ?? 0);
            const phMask = String((clip as any)?.maskShape ?? "none");

            const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
            const computeCoverCropFromFocus = (args: {
                srcAspect: number;
                targetAspect: number;
                focusRect: { x: number; y: number; w: number; h: number } | null;
            }): { x: number; y: number; width: number; height: number } | undefined => {
                const srcAspect = args.srcAspect;
                const targetAspect = args.targetAspect;
                if (!Number.isFinite(srcAspect) || !Number.isFinite(targetAspect) || targetAspect <= 0) return undefined;

                let cropW = 1;
                let cropH = 1;
                if (srcAspect > targetAspect) {
                    cropW = clamp01(targetAspect / srcAspect);
                    cropH = 1;
                } else {
                    cropW = 1;
                    cropH = clamp01(srcAspect / targetAspect);
                }

                const focus = args.focusRect;
                const fx0 = clamp01(Number(focus?.x ?? 0.5));
                const fy0 = clamp01(Number(focus?.y ?? 0.5));
                const fw = clamp01(Number(focus?.w ?? 0));
                const fh = clamp01(Number(focus?.h ?? 0));
                const fx1 = clamp01(fx0 + fw);
                const fy1 = clamp01(fy0 + fh);
                const fcx = clamp01(fx0 + (fx1 - fx0) / 2);
                const fcy = clamp01(fy0 + (fy1 - fy0) / 2);

                const minX = Math.max(0, fx1 - cropW);
                const maxX = Math.min(1 - cropW, fx0);
                const minY = Math.max(0, fy1 - cropH);
                const maxY = Math.min(1 - cropH, fy0);

                const desiredX = fcx - cropW / 2;
                const desiredY = fcy - cropH / 2;

                const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
                const x = minX <= maxX ? clamp(desiredX, minX, maxX) : clamp(desiredX, 0, 1 - cropW);
                const y = minY <= maxY ? clamp(desiredY, minY, maxY) : clamp(desiredY, 0, 1 - cropH);

                if (cropW >= 0.999 && cropH >= 0.999) return undefined;
                return { x, y, width: cropW, height: cropH };
            };

            const trackRecord = ProjectAssetsStore.projectTracks.find((t: any) => String(t?.id ?? t?.trackId ?? "") === assetId);
            const imageRecord = ProjectAssetsStore.projectImages.find((a: any) => String(a?.id ?? "") === assetId);

            const type = replaceKind === "image" ? "image" : "video";
            const assetTitle =
                replaceKind === "image"
                    ? String(imageRecord?.title ?? imageRecord?.name ?? "Image")
                    : String(trackRecord?.title ?? trackRecord?.filename ?? "Video");
            const assetResolution = replaceKind === "image"
                ? String(imageRecord?.resolution ?? imageRecord?.metadata?.resolution ?? "")
                : String(trackRecord?.resolution ?? "");
            const thumbnailId = replaceKind === "image" ? String(imageRecord?.thumbnailId ?? "") : undefined;

            const newClip: any = await CreateClipForComposition({
                type,
                thumbnailId,
                assetId,
                assetTitle,
                assetResolution,
                composition: CompositionService.composition as any,
                stateStore: CompositionStateStore as any,
                resolveAssetUrl: ResolveAssetUrl as any,
            });
            if (!newClip) return;

            // Align to placeholder range on timeline.
            try {
                if ("range" in newClip && "offset" in newClip) {
                    const inSec = Number((newClip as any)?.range?.[0]?.seconds ?? 0);
                    (newClip as any).offset = Timestamp.fromSeconds(timing.startSec - inSec);

                    // Trim to placeholder duration (best-effort; clamp to known source duration when available).
                    const rate = Number((newClip as any)?.playbackRate ?? 1) || 1;
                    const desiredOutSrc = inSec + placeholderDur * rate;
                    let maxOutSrc = desiredOutSrc;
                    if (replaceKind !== "image") {
                        const naturalSrcDur = Number(trackRecord?.duration ?? trackRecord?.Duration ?? trackRecord?.durationSec ?? NaN);
                        if (Number.isFinite(naturalSrcDur) && naturalSrcDur > 0) {
                            maxOutSrc = inSec + naturalSrcDur;
                        }
                    }
                    const outSrc = Number.isFinite(maxOutSrc) ? Math.min(desiredOutSrc, maxOutSrc) : desiredOutSrc;
                    try {
                        if (replaceKind === "image" && typeof (newClip as any)?.duration?.seconds === "number") {
                            // ImageClip renders only while t < duration.seconds; extend it to match the placeholder.
                            (newClip as any).duration.seconds = outSrc;
                            (newClip as any).range[1].seconds = outSrc;
                        } else {
                            (newClip as any).range[1] = Timestamp.fromSeconds(outSrc);
                        }
                    } catch {}
                } else {
                    (newClip as any).start = Timestamp.fromSeconds(timing.startSec);
                    (newClip as any).stop = Timestamp.fromSeconds(timing.stopSec);
                }
            } catch {}

            // Adopt placeholder's current slot geometry (and mask shape) so it lands in the same place.
            try { if (Number.isFinite(phX) && Number.isFinite(phY)) (newClip as any).position = { x: phX, y: phY } as any; } catch {}
            try { if (Number.isFinite(phW) && phW > 0) (newClip as any).width = phW as any; } catch {}
            try { if (Number.isFinite(phH) && phH > 0) (newClip as any).height = phH as any; } catch {}
            try { if (typeof (newClip as any).maskShape === "string") (newClip as any).maskShape = phMask as any; } catch {}

            // Best-effort cover crop to match the placeholder aspect, using quick focus if available.
            try {
                const targetAspect = phW > 0 && phH > 0 ? phW / phH : NaN;
                const resMatch = String(assetResolution ?? "").match(/(\d+)x(\d+)/);
                const srcW = resMatch ? Number(resMatch[1]) : NaN;
                const srcH = resMatch ? Number(resMatch[2]) : NaN;
                const srcAspect = srcW > 0 && srcH > 0 ? srcW / srcH : NaN;
                const focusRect = FocusStore.getQuickSummary(assetId)?.rect ?? null;
                if (Number.isFinite(srcAspect) && Number.isFinite(targetAspect) && targetAspect > 0) {
                    const crop = computeCoverCropFromFocus({
                        srcAspect,
                        targetAspect,
                        focusRect: focusRect ? { x: focusRect.x, y: focusRect.y, w: focusRect.w, h: focusRect.h } : null,
                    });
                    if (typeof (newClip as any)?.setCrop === "function") (newClip as any).setCrop(crop);
                }
            } catch {}

            // Insert new clip on a NEW track (avoid mixing placeholders with video/image tracks).
            // Then position the new track at the placeholder track's z-order before deleting the placeholder,
            // so the replacement visually stays in the same stacking slot.
            let newTrack: any = null;
            try {
                newTrack = await (CompositionService.composition as any).add(newClip as any);
            } catch {
                // Best-effort fallback: if insertion fails, bail (don't delete placeholder).
                return;
            }
            try {
                if (placeholderTrackIndex >= 0 && newTrack && typeof newTrack.layer === "function") {
                    // add() unshifts the new track to index 0, so placeholder track shifts +1.
                    newTrack.layer(placeholderTrackIndex + 1);
                }
            } catch {}

            // Best-effort: copy placeholder's current scene overrides to the new clip so it lands in the slot.
            try {
	                const ovs = (clip as any)?.sceneVisualOverridesByAspect ?? (clip as any)?.sceneVisualOverrides;
	                if (ovs && typeof ovs === "object") {
	                    (newClip as any).sceneVisualOverridesByAspect = JSON.parse(JSON.stringify(ovs));
	                }
	            } catch {}

            // If placeholder is selected, select the new clip.
            try { ApplicationStore.setSelectedClipId(String((newClip as any).id)); } catch {}

            // Remove placeholder after insertion.
            try { CompositionService.deleteClipById(String(clip.id)); } catch {}

            closeReplaceDialog();
        } catch (e) {
            console.warn("[Placeholder] replace failed", e);
	        }
	    };
	    */

	    const layoutOptions = (() => {
	        const pack = LayoutPacksStore.getPack(activeLayoutPackId) ?? BUILTIN_LAYOUT_PACK;
	        const entries = pack.pickerEntriesByPreset?.[canvasAspectPreset] ?? [];

	        const multicamVariants = (pack.multicamVariantsByPreset?.[canvasAspectPreset] ??
	            BUILTIN_LAYOUT_PACK.multicamVariantsByPreset?.[canvasAspectPreset] ??
	            []) as MulticamVariant[];
	        const defaultMulticamKey =
	            String(pack.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "").trim() ||
	            String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "two");
	        const multicamKey = String(multicamVariantKeyByPreset[canvasAspectPreset] ?? defaultMulticamKey);
	        const multicamSelected = multicamVariants.find((v) => v.key === multicamKey) ?? multicamVariants[0];

	        return entries.map((layoutId: string) => {
	            if (layoutId === "multicam") {
	                const urls = previewToUrls(multicamSelected as any, canvasAspectPreset);
	                return {
	                    layoutId,
	                    name: "Multicam",
	                    previewObjectFit: canvasAspectPreset === "horizontal" ? ("cover" as const) : ("contain" as const),
	                    ...(urls ?? {}),
	                };
	            }

	            const tmpl = getLayoutTemplate(pack, layoutId) ?? getLayoutTemplate(BUILTIN_LAYOUT_PACK, layoutId);
	            const preview = tmpl?.previewsByPreset?.[canvasAspectPreset];
	            const urls = previewToUrls(preview as any, canvasAspectPreset);
	            return {
	                layoutId,
	                name: tmpl?.title ?? layoutId,
	                previewObjectFit:
	                    (preview?.objectFit as any) ??
	                    (canvasAspectPreset === "horizontal" ? ("cover" as const) : ("contain" as const)),
	                ...(urls ?? {}),
	            };
	        });
	    })();

    const hasTransitions = Boolean(sceneTransitions.in || sceneTransitions.out);
    const activeTransition = transitionControlsEdge ? sceneTransitions[transitionControlsEdge] : null;

    const normalizeDurationInput = (value: string): string => {
        const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
        if (!cleaned) return "";

        const firstDotIndex = cleaned.indexOf(".");
        if (firstDotIndex === -1) return cleaned;

        const wholeRaw = cleaned.slice(0, firstDotIndex);
        const whole = wholeRaw.length > 0 ? wholeRaw : "0";
        const rest = cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
        const nextFrac = rest.slice(0, 1);

        if (cleaned.endsWith(".") && nextFrac.length === 0) return `${whole}.`;
        return nextFrac.length > 0 ? `${whole}.${nextFrac}` : whole;
    };

    const finalizeDurationValue = (value: string): string => {
        const num = Number(value);
        if (!Number.isFinite(num)) return DEFAULT_TRANSITION_DURATION;
        return num.toFixed(1);
    };

    const buildTransitionState = (type: TransitionType): TransitionEdgeState => ({
        type,
        duration: DEFAULT_TRANSITION_DURATION,
        easing: DEFAULT_SMART_EASING,
        addFades: false,
    });

    const transitionSelectMenuProps = {
        sx: {
            zIndex: 1700,
        },
        PaperProps: {
            sx: {
                minWidth: 180,
                padding: "6px",
                backgroundColor: "var(--app-bg-surface)",
                borderRadius: "10px",
                border: "1px solid var(--app-border-subtle)",
                boxShadow: "0 18px 40px rgba(0, 0, 0, 0.16)",
                "& .MuiList-root": {
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                },
            },
        },
        MenuListProps: {
            sx: {
                padding: 0,
            },
        },
    };

    const transitionMenuItemSx = {
        fontFamily: "Inter, sans-serif",
        fontSize: "13px",
        fontWeight: 400,
        color: "var(--app-text-primary)",
        padding: "6px 10px",
        borderRadius: "8px",
        minHeight: "auto",
        "&:hover": {
            backgroundColor: "var(--app-bg-subtle)",
        },
    };

    const transitionSelectSx = {
        flex: 1,
        backgroundColor: "var(--app-bg-subtle)",
        borderRadius: "8px",
        "& .MuiSelect-select": { py: 0.5, fontSize: 13, fontWeight: 500 },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--app-border-subtle)" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--app-border-muted)" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2B6BFD" },
        "& .MuiSelect-icon": { color: "var(--app-text-muted)" },
        "& .MuiSelect-select, & .MuiSelect-select.MuiSelect-outlined": { color: "var(--app-text-primary)" },
    };

    const openTransitionMenu = (event: React.MouseEvent<HTMLElement>) => {
        setTransitionMenuAnchorEl(event.currentTarget);
        setTransitionMenuOpen((prev) => !prev);
        setTransitionMenuHover(null);
        setTransitionSubmenuHover(false);
    };

    const closeTransitionMenu = () => {
        setTransitionMenuOpen(false);
        setTransitionMenuHover(null);
        setTransitionSubmenuHover(false);
    };

    const handleSelectTransition = (edge: "in" | "out", value: TransitionType) => {
        const nextState = buildTransitionState(value);
        setSceneTransitions((prev) => ({ ...prev, [edge]: nextState }));
        applySceneTransition(edge, nextState);
        closeTransitionMenu();
    };

    const handleRemoveTransition = (edge: "in" | "out") => {
        setSceneTransitions((prev) => ({ ...prev, [edge]: null }));
        applySceneTransition(edge, null);
        if (transitionControlsEdge === edge) {
            setTransitionControlsEdge(null);
            setTransitionControlsAnchorEl(null);
        }
    };

    const handleTransitionTypeChange = (edge: "in" | "out", nextType: TransitionType) => {
        setSceneTransitions((prev) => {
            const current = prev[edge];
            const next: TransitionEdgeState = {
                type: nextType,
                duration: current?.duration ?? DEFAULT_TRANSITION_DURATION,
                easing: current?.easing ?? DEFAULT_SMART_EASING,
                addFades: current?.addFades ?? false,
            };
            // Apply to engine as well.
            applySceneTransition(edge, next);
            return { ...prev, [edge]: next };
        });
    };

    const updateTransitionDuration = (edge: "in" | "out", value: string, finalize = false) => {
        setSceneTransitions((prev) => {
            const current = prev[edge];
            if (!current) return prev;
            const nextValue = finalize ? finalizeDurationValue(value) : normalizeDurationInput(value);
            const next: TransitionEdgeState = { ...current, duration: nextValue };
            if (finalize) applySceneTransition(edge, next);
            return { ...prev, [edge]: next };
        });
    };

    const updateTransitionEasing = (edge: "in" | "out", easing: string) => {
        setSceneTransitions((prev) => {
            const current = prev[edge];
            if (!current) return prev;
            const next: TransitionEdgeState = { ...current, easing };
            applySceneTransition(edge, next);
            return { ...prev, [edge]: next };
        });
    };

    const updateTransitionAddFades = (edge: "in" | "out", addFades: boolean) => {
        setSceneTransitions((prev) => {
            const current = prev[edge];
            if (!current) return prev;
            const next: TransitionEdgeState = { ...current, addFades };
            applySceneTransition(edge, next);
            return { ...prev, [edge]: next };
        });
    };

    const openTransitionControls = (edge: "in" | "out", event: React.MouseEvent<HTMLElement>) => {
        setTransitionControlsEdge(edge);
        setTransitionControlsAnchorEl(event.currentTarget);
    };

    const closeTransitionControls = () => {
        setTransitionControlsEdge(null);
        setTransitionControlsAnchorEl(null);
    };

    // Local UI state derived from clip
    const [rate, setRate] = useState<number>(1);
    const [gainPct, setGainPct] = useState<number>(100); // map to 0..4 linear gain (see below)
    const [opacityPct, setOpacityPct] = useState<number>(100);
    const [studioSound, setStudioSound] = useState<boolean>(false);
    const [studioSoundSwitching, setStudioSoundSwitching] = useState<boolean>(false);
    const [studioSoundPendingEnable, setStudioSoundPendingEnable] = useState<boolean>(false);
    const [posX, setPosX] = useState<string>('0');
    const [posY, setPosY] = useState<string>('0');
    const [wStr, setWStr] = useState<string>('');
    const [hStr, setHStr] = useState<string>('');
    const [clipAnimations, setClipAnimations] = useState<ClipAnimationEntry[]>([]);
    const [animationMenuAnchorEl, setAnimationMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [animationControlsAnchorEl, setAnimationControlsAnchorEl] = useState<HTMLElement | null>(null);
    const [animationControlsId, setAnimationControlsId] = useState<string | null>(null);
    const [animationStartDraft, setAnimationStartDraft] = useState<string>("");
    const [animationEndDraft, setAnimationEndDraft] = useState<string>("");
    const [activeKeyframeAnimationId, setActiveKeyframeAnimationId] = useState<string | null>(null);
    const [animationError, setAnimationError] = useState<string>("");
    const lastNonZeroGainPctByKeyRef = useRef<Record<string, number>>({});
    const deferredWaveformGainByClipIdRef = useRef<Map<string, number>>(new Map());
    const deferredWaveformSyncTimerRef = useRef<number | null>(null);
    const deferredTimingChangedRef = useRef<boolean>(false);
    const deferredTimingChangedTimerRef = useRef<number | null>(null);
    const deferredClipUpdatedByIdRef = useRef<Map<string, any>>(new Map());
    const deferredClipUpdatedTimerRef = useRef<number | null>(null);
    const deferredGainCommitTimerRef = useRef<number | null>(null);

    const flushDeferredWaveformSync = () => {
        if (deferredWaveformSyncTimerRef.current !== null) {
            window.clearTimeout(deferredWaveformSyncTimerRef.current);
            deferredWaveformSyncTimerRef.current = null;
        }

        if (deferredWaveformGainByClipIdRef.current.size > 0) {
            for (const [clipId, gain] of deferredWaveformGainByClipIdRef.current.entries()) {
                try { ScriptLayerStore.setClipGain(clipId, gain); } catch {}
            }
            deferredWaveformGainByClipIdRef.current.clear();
        }
    };

    const scheduleDeferredWaveformSync = (delayMs: number = 140) => {
        if (deferredWaveformSyncTimerRef.current !== null) {
            window.clearTimeout(deferredWaveformSyncTimerRef.current);
        }
        deferredWaveformSyncTimerRef.current = window.setTimeout(() => {
            deferredWaveformSyncTimerRef.current = null;
            flushDeferredWaveformSync();
        }, delayMs);
    };

    const queueDeferredWaveformGainSync = (clipId: string, gain: number) => {
        if (!clipId) return;
        deferredWaveformGainByClipIdRef.current.set(String(clipId), gain);
        scheduleDeferredWaveformSync();
    };

    const queueDeferredTimingChanged = (delayMs: number = 120) => {
        deferredTimingChangedRef.current = true;
        if (deferredTimingChangedTimerRef.current !== null) {
            window.clearTimeout(deferredTimingChangedTimerRef.current);
        }
        deferredTimingChangedTimerRef.current = window.setTimeout(() => {
            deferredTimingChangedTimerRef.current = null;
            if (!deferredTimingChangedRef.current) return;
            deferredTimingChangedRef.current = false;
            try { CompositionService.composition.onClipTimingChanged(); } catch {}
        }, delayMs);
    };

    const queueDeferredClipUpdated = (targetClip: any, delayMs: number = 160) => {
        const clipId = String(targetClip?.id ?? "");
        if (!clipId) return;
        deferredClipUpdatedByIdRef.current.set(clipId, targetClip);
        if (deferredClipUpdatedTimerRef.current !== null) {
            window.clearTimeout(deferredClipUpdatedTimerRef.current);
        }
        deferredClipUpdatedTimerRef.current = window.setTimeout(() => {
            deferredClipUpdatedTimerRef.current = null;
            const pending = Array.from(deferredClipUpdatedByIdRef.current.values());
            deferredClipUpdatedByIdRef.current.clear();
            for (const c of pending) {
                try { CompositionService.notifyClipUpdated(c); } catch {}
            }
        }, delayMs);
    };

    const cancelDeferredGainCommit = () => {
        if (deferredGainCommitTimerRef.current !== null) {
            window.clearTimeout(deferredGainCommitTimerRef.current);
            deferredGainCommitTimerRef.current = null;
        }
    };

    const cancelDeferredGainFollowUpWork = () => {
        if (deferredWaveformSyncTimerRef.current !== null) {
            window.clearTimeout(deferredWaveformSyncTimerRef.current);
            deferredWaveformSyncTimerRef.current = null;
        }
        deferredWaveformGainByClipIdRef.current.clear();

        if (deferredTimingChangedTimerRef.current !== null) {
            window.clearTimeout(deferredTimingChangedTimerRef.current);
            deferredTimingChangedTimerRef.current = null;
        }
        deferredTimingChangedRef.current = false;

        if (deferredClipUpdatedTimerRef.current !== null) {
            window.clearTimeout(deferredClipUpdatedTimerRef.current);
            deferredClipUpdatedTimerRef.current = null;
        }
        deferredClipUpdatedByIdRef.current.clear();
    };

    useEffect(() => {
        return () => {
            if (deferredGainCommitTimerRef.current !== null) {
                window.clearTimeout(deferredGainCommitTimerRef.current);
                deferredGainCommitTimerRef.current = null;
            }

            if (deferredWaveformSyncTimerRef.current !== null) {
                window.clearTimeout(deferredWaveformSyncTimerRef.current);
                deferredWaveformSyncTimerRef.current = null;
            }

            if (deferredWaveformGainByClipIdRef.current.size > 0) {
                for (const [clipId, gain] of deferredWaveformGainByClipIdRef.current.entries()) {
                    try { ScriptLayerStore.setClipGain(clipId, gain); } catch {}
                }
                deferredWaveformGainByClipIdRef.current.clear();
            }

            if (deferredTimingChangedTimerRef.current !== null) {
                window.clearTimeout(deferredTimingChangedTimerRef.current);
                deferredTimingChangedTimerRef.current = null;
            }
            if (deferredTimingChangedRef.current) {
                deferredTimingChangedRef.current = false;
                try { CompositionService.composition.onClipTimingChanged(); } catch {}
            }

            if (deferredClipUpdatedTimerRef.current !== null) {
                window.clearTimeout(deferredClipUpdatedTimerRef.current);
                deferredClipUpdatedTimerRef.current = null;
            }
            if (deferredClipUpdatedByIdRef.current.size > 0) {
                const pending = Array.from(deferredClipUpdatedByIdRef.current.values());
                deferredClipUpdatedByIdRef.current.clear();
                for (const c of pending) {
                    try { CompositionService.notifyClipUpdated(c); } catch {}
                }
            }
        };
    }, []);

    useEffect(() => {
        cancelDeferredGainCommit();
    }, [clip?.id, selectedSceneIndex, playheadSceneIndex]);

    const persistClipAnimations = (nextAnimations: ClipAnimationEntry[]): ClipAnimationEntry[] => {
        if (!clip) {
            setClipAnimations([]);
            return [];
        }
        const bounds = getClipTimelineBoundsSec(clip);
        const sanitized = sanitizeClipAnimations(nextAnimations, bounds);
        setClipAnimations(sanitized);
        try { (clip as any)[CLIP_ANIMATION_STORE_KEY] = sanitized; } catch {}
        try { (clip as any).trigger?.("frame", undefined); } catch {}
        try { CompositionService.notifyClipUpdated(clip); } catch {}
        return sanitized;
    };

    const closeAnimationControlsPanel = () => {
        setAnimationControlsId(null);
        setAnimationControlsAnchorEl(null);
    };

    const commitAnimationBoundary = (edge: "start" | "end") => {
        if (!clip || !animationControlsId) return;
        const existing = clipAnimations.find((anim) => anim.id === animationControlsId);
        if (!existing) return;

        const parsed = parseAnimationTimestamp(edge === "start" ? animationStartDraft : animationEndDraft);
        if (!Number.isFinite(parsed)) {
            setAnimationStartDraft(formatAnimationTimestamp(existing.startSec));
            setAnimationEndDraft(formatAnimationTimestamp(existing.endSec));
            return;
        }

        const bounds = getClipTimelineBoundsSec(clip);
        let nextStart = existing.startSec;
        let nextEnd = existing.endSec;
        const clamped = Math.max(bounds.startSec, Math.min(bounds.endSec, Number(parsed)));

        if (edge === "start") {
            nextStart = Math.min(clamped, nextEnd - CLIP_ANIMATION_MIN_DURATION_SEC);
        } else {
            nextEnd = Math.max(clamped, nextStart + CLIP_ANIMATION_MIN_DURATION_SEC);
        }

        nextStart = Math.max(bounds.startSec, nextStart);
        nextEnd = Math.min(bounds.endSec, nextEnd);

        if (nextEnd - nextStart < CLIP_ANIMATION_MIN_DURATION_SEC) {
            setAnimationError("Animation must be at least 0.1s long.");
            setAnimationStartDraft(formatAnimationTimestamp(existing.startSec));
            setAnimationEndDraft(formatAnimationTimestamp(existing.endSec));
            return;
        }

        if (hasAnimationOverlap(clipAnimations, nextStart, nextEnd, existing.id)) {
            setAnimationError("Animation cannot overlap another animation.");
            setAnimationStartDraft(formatAnimationTimestamp(existing.startSec));
            setAnimationEndDraft(formatAnimationTimestamp(existing.endSec));
            return;
        }

        const updated = persistClipAnimations(
            clipAnimations.map((anim) =>
                anim.id === existing.id
                    ? { ...anim, startSec: nextStart, endSec: nextEnd }
                    : anim
            )
        );
        const next = updated.find((anim) => anim.id === existing.id);
        if (next) {
            setAnimationStartDraft(formatAnimationTimestamp(next.startSec));
            setAnimationEndDraft(formatAnimationTimestamp(next.endSec));
        }
        setAnimationError("");
    };

    const handleCreateCustomAnimation = () => {
        if (!clip) return;

        const bounds = getClipTimelineBoundsSec(clip);
        const playheadSec = Number(CompositionStateStore.currentTime ?? 0);
        const startSec = Math.max(bounds.startSec, Math.min(bounds.endSec, playheadSec));
        const endSec = Math.min(bounds.endSec, startSec + CLIP_ANIMATION_DEFAULT_DURATION_SEC);

        if (endSec - startSec < CLIP_ANIMATION_MIN_DURATION_SEC) {
            setAnimationError("Not enough remaining clip duration to create an animation.");
            setAnimationMenuAnchorEl(null);
            return;
        }

        if (hasAnimationOverlap(clipAnimations, startSec, endSec)) {
            setAnimationError("Cannot create overlapping animation.");
            setAnimationMenuAnchorEl(null);
            return;
        }

        const nextAnimation: ClipAnimationEntry = {
            id: createClipAnimationId(),
            kind: "custom",
            startSec,
            endSec,
            easing: DEFAULT_SMART_EASING,
        };

        persistClipAnimations([...clipAnimations, nextAnimation]);
        setAnimationError("");
        setAnimationMenuAnchorEl(null);
    };

    const handleOpenAnimationControls = (animationId: string, event: React.MouseEvent<HTMLElement>) => {
        const target = clipAnimations.find((anim) => anim.id === animationId);
        if (!target) return;
        setAnimationControlsId(animationId);
        setAnimationControlsAnchorEl(event.currentTarget);
        setAnimationStartDraft(formatAnimationTimestamp(target.startSec));
        setAnimationEndDraft(formatAnimationTimestamp(target.endSec));
        setAnimationError("");
    };

    const handleAnimationEasingChange = (animationId: string, easing: string) => {
        persistClipAnimations(
            clipAnimations.map((anim) => (anim.id === animationId ? { ...anim, easing } : anim))
        );
        setAnimationError("");
    };

    const handleRemoveAnimation = (animationId: string) => {
        persistClipAnimations(clipAnimations.filter((anim) => anim.id !== animationId));
        if (animationControlsId === animationId) closeAnimationControlsPanel();
        if (activeKeyframeAnimationId === animationId) setActiveKeyframeAnimationId(null);
        setAnimationError("");
    };

    const handleToggleKeyframeModeStub = (animationId: string) => {
        setActiveKeyframeAnimationId((prev) => (prev === animationId ? null : animationId));
        setAnimationError("");
    };

    const animationControlTarget = animationControlsId
        ? clipAnimations.find((anim) => anim.id === animationControlsId) ?? null
        : null;

    useEffect(() => {
        if (!clip) {
            setClipAnimations([]);
            setAnimationMenuAnchorEl(null);
            closeAnimationControlsPanel();
            setActiveKeyframeAnimationId(null);
            setAnimationError("");
            return;
        }

        const bounds = getClipTimelineBoundsSec(clip);
        const sanitized = sanitizeClipAnimations((clip as any)?.[CLIP_ANIMATION_STORE_KEY], bounds);
        setClipAnimations(sanitized);
        try { (clip as any)[CLIP_ANIMATION_STORE_KEY] = sanitized; } catch {}
        setAnimationError("");
    }, [clip?.id]);

    useEffect(() => {
        if (!animationControlsId) return;
        const target = clipAnimations.find((anim) => anim.id === animationControlsId);
        if (!target) {
            closeAnimationControlsPanel();
            return;
        }
        setAnimationStartDraft(formatAnimationTimestamp(target.startSec));
        setAnimationEndDraft(formatAnimationTimestamp(target.endSec));
    }, [animationControlsId, clipAnimations]);

    // Keep Studio Sound toggle UI in sync with the clip state (including restores/undo).
    useEffect(() => {
        if (!clip) {
            setStudioSoundPendingEnable(false);
            return;
        }
        try {
            setStudioSound(Boolean((clip as any).studioSoundEnabled));
        } catch {}
        setStudioSoundPendingEnable(false);

        const onChanged = (evt: any) => {
            try {
                if (typeof evt?.enabled === "boolean") {
                    setStudioSound(evt.enabled);
                } else {
                    setStudioSound(Boolean((clip as any).studioSoundEnabled));
                }
                if (evt?.enabled === true) {
                    setStudioSoundPendingEnable(false);
                }
            } catch {}
        };

        try { (clip as any).on?.("studioSoundChanged", onChanged); } catch {}
        return () => {
            try { (clip as any).off?.("studioSoundChanged", onChanged); } catch {}
        };
    }, [clip]);

    useEffect(() => {
        if (!clip) return;

        // For script clips, determine effective scene index
        // Use selectedSceneIndex if explicitly selected, otherwise use playheadSceneIndex
        const effectiveSceneIndex = isScriptClip(clip)
            ? (selectedSceneIndex ?? playheadSceneIndex)
            : null;
        const isEditingSceneProps = effectiveSceneIndex !== null;

        // Playback rate: scene-specific for script clips, clip-level otherwise
        if (isEditingSceneProps && isScriptClip(clip)) {
            const sceneRate = clip.getEffectivePlaybackRateForScene?.(effectiveSceneIndex) ?? 1;
            setRate(sceneRate);
        } else if (typeof clip.playbackRate === 'number') {
            setRate(clip.playbackRate || 1);
        }

        // Determine effective gain based on scene selection
        let g: number;
        if (isEditingSceneProps && isScriptClip(clip)) {
            const sceneGain = clip.getEffectiveGainForScene?.(effectiveSceneIndex);
            g = sceneGain ?? clip.gain ?? 1;
        } else {
            g = typeof clip.gain === 'number' ? clip.gain : (typeof clip.volume === 'number' ? clip.volume : 1);
        }
        const nextGainPct = Math.round(Math.min(4, Math.max(0, g)) * 100);
        setGainPct(nextGainPct);
        // Keep a "last non-zero" gain to support mute/unmute toggles.
        if (clip.id && nextGainPct > 0) {
            const key = isScriptClip(clip)
                ? `${clip.id}:scene:${String(selectedSceneIndex ?? playheadSceneIndex ?? "clip")}`
                : String(clip.id);
            lastNonZeroGainPctByKeyRef.current[key] = nextGainPct;
        }

        // Initialize observable store with clip's gain
        if (clip.id) {
            ScriptLayerStore.setClipGain(clip.id, clip.gain ?? 1);
        }

        // Get visual properties - optionally scene-specific based on the global scope toggle.
        const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
        const useSceneOverrides = sceneEditScope === "scene" && spansMultipleScenes;
        let visualProps: { alpha: number; x: number; y: number; width: number; height: number } | null = null;

        if (useSceneOverrides && isScriptClip(clip) && isEditingSceneProps) {
            const effectiveSourceIndex = sourceIndex ?? 0;
            visualProps = clip.getSceneVisualProperties?.(effectiveSceneIndex, effectiveSourceIndex) ?? null;
        }

        // Base/source-level properties
        visualProps = visualProps ?? getVisualProps(clip, sourceIndex);

        // Non-script visual clips: merge per-scene overrides when editing Current scene.
	        if (useSceneOverrides && visualProps && !isScriptClip(clip)) {
	            const sceneKey = CompositionService.getCurrentSceneKey();
	            const ovs =
	                sceneKey
	                    ? (clip as any)?.sceneVisualOverridesByAspect?.[canvasAspectPreset]?.[sceneKey] ??
	                      (clip as any)?.sceneVisualOverrides?.[sceneKey]
	                    : null;
	            if (ovs && typeof ovs === "object") {
	                const merged: any = { ...visualProps };
	                for (const k of ["x", "y", "width", "height", "alpha", "rotation"]) {
	                    const num = (ovs as any)[k];
                    if (typeof num === "number" && Number.isFinite(num)) merged[k] = num;
                }
                visualProps = merged;
            }
        }

        if (visualProps) {
            const toFiniteNumber = (value: unknown, fallback: number) => {
                const n = typeof value === "number" ? value : Number(value);
                return Number.isFinite(n) ? n : fallback;
            };

            const alpha = toFiniteNumber((visualProps as any).alpha, 1);
            const x = toFiniteNumber((visualProps as any).x, 0);
            const y = toFiniteNumber((visualProps as any).y, 0);
            const width = toFiniteNumber((visualProps as any).width, 0);
            const height = toFiniteNumber((visualProps as any).height, 0);

            setOpacityPct(Math.round(Math.min(1, Math.max(0, alpha)) * 100));
            setPosX(x.toFixed(1));
            setPosY(y.toFixed(1));
            setWStr(String(Math.round(width)));
            setHStr(String(Math.round(height)));
        } else {
            setOpacityPct(100);
            setPosX('0');
            setPosY('0');
            setWStr('');
            setHStr('');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clip, sourceIndex, selectedSceneIndex, playheadSceneIndex, sceneEditScope, ScriptLayerStore.visualPropsVersion]);

    // Helper to apply visual properties (handles scene-specific vs source-level)
    const applyVisualProp = (props: Partial<{ x: number; y: number; width: number; height: number; alpha: number }>) => {
        if (!clip) return;

        const keys = Object.keys(props) as Array<"x" | "y" | "width" | "height" | "alpha">;
        const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
        const useSceneOverrides = sceneEditScope === "scene" && spansMultipleScenes;
        const useAllScope = sceneEditScope === "all" && spansMultipleScenes;

        // Script clip video sources: per-scene via sceneProperties; base via video entry.
        if (isScriptClip(clip)) {
            const effectiveSourceIndex = sourceIndex ?? 0;
            const effectiveSceneIndex = selectedSceneIndex ?? playheadSceneIndex;

            if (useAllScope) {
                try {
                    (clip as any).clearSceneVisualOverrideKeysForSource?.(effectiveSourceIndex, keys);
                } catch {}
                try {
                    (clip as any).updateVideoSourceTransform?.(effectiveSourceIndex, props);
                } catch {}
                ScriptLayerStore.bumpCutsVersion();
            } else if (useSceneOverrides && effectiveSceneIndex !== null) {
                (clip as any).setSceneVisualProperties?.(effectiveSceneIndex, effectiveSourceIndex, props);
                ScriptLayerStore.bumpCutsVersion();
            } else {
                // Single-scene / no scene context: apply to base.
                try {
                    (clip as any).updateVideoSourceTransform?.(effectiveSourceIndex, props);
                } catch {}
                ScriptLayerStore.bumpCutsVersion();
            }

            try { CompositionService.composition.computeFrame(); } catch {}
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
            return;
        }

        // Non-script visual clips: apply per-scene overrides when enabled, otherwise apply globally.
        if (useSceneOverrides) {
            const sceneKey = CompositionService.getCurrentSceneKey();
            if (sceneKey) {
                CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, props);
            }
        } else {
            if (useAllScope) {
                CompositionService.clearSceneVisualOverrideKeysForClip(clip, keys);
            }
            if (props.x !== undefined) (clip as any).x = props.x;
            if (props.y !== undefined) (clip as any).y = props.y;
            if (props.width !== undefined) (clip as any).width = props.width;
            if (props.height !== undefined) (clip as any).height = props.height;
            if (props.alpha !== undefined) (clip as any).alpha = props.alpha;
        }

        try { (clip as any).trigger?.("frame", undefined); } catch {}
        try { CompositionService.composition.computeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
    };

    // Commit helpers for size/position fields (apply only on Enter or blur)
    const commitPosX = () => {
        if (!clip) return;
        const v = (posX ?? '').trim();
        if (v === '') return;
        let num = Number(v);
        if (!Number.isFinite(num)) return;
        num = Math.round(num * 10) / 10;
        applyVisualProp({ x: num });
        setPosX(num.toFixed(1));
        try { CompositionService.notifyClipUpdated(clip); } catch {}
    };
    const commitPosY = () => {
        if (!clip) return;
        const v = (posY ?? '').trim();
        if (v === '') return;
        let num = Number(v);
        if (!Number.isFinite(num)) return;
        num = Math.round(num * 10) / 10;
        applyVisualProp({ y: num });
        setPosY(num.toFixed(1));
        try { CompositionService.notifyClipUpdated(clip); } catch {}
    };
    const commitWidth = () => {
        if (!clip) return;
        const v = (wStr ?? '').trim();
        if (v === '') return;
        const num = Number(v);
        if (!Number.isFinite(num) || num <= 0) return;
        applyVisualProp({ width: Math.round(num) });
        setWStr(String(Math.round(num)));
        try { CompositionService.notifyClipUpdated(clip); } catch {}
    };
    const commitHeight = () => {
        if (!clip) return;
        const v = (hStr ?? '').trim();
        if (v === '') return;
        const num = Number(v);
        if (!Number.isFinite(num) || num <= 0) return;
        applyVisualProp({ height: Math.round(num) });
        setHStr(String(Math.round(num)));
        try { CompositionService.notifyClipUpdated(clip); } catch {}
    };

    const applyPlaybackRate = (newRate: number) => {
        if (!clip) return;
        setRate(newRate);

        // For script clips, apply to scene-specific properties
        // Use selectedSceneIndex if explicitly selected, otherwise use playheadSceneIndex
        if (isScriptClip(clip)) {
            const scenes = clip.getScenes?.() ?? [];
            const effectiveSceneIndex = selectedSceneIndex ?? playheadSceneIndex;

            // For single-scene case (no splits), set clip-level playbackRate
            // This ensures the audio scheduler picks it up correctly
            if (scenes.length <= 1) {
                clip.playbackRate = newRate;
                ScriptLayerStore.setPlaybackRate(newRate); // Sync for transcript ↔ timeline time mapping
                ScriptLayerStore.bumpCutsVersion();
                return;
            }

            // For multi-scene case, set scene-specific properties
            if (effectiveSceneIndex !== null) {
                clip.setSceneProperties(effectiveSceneIndex, { playbackRate: newRate });
                ScriptLayerStore.bumpCutsVersion(); // Trigger re-render and timeline update
                // Trigger timing recalculation
                try {
                    CompositionService.composition.onClipTimingChanged();
                } catch {}
                try { CompositionService.notifyClipUpdated(clip); } catch {}
                return;
            }
        }

        // Otherwise apply to clip-level
        if (typeof clip.playbackRate === 'number' || clip.playbackRate === undefined) {
            clip.playbackRate = newRate;
            // MediaClip setter already triggers timing changed + UI updates
        }
    };

    const rememberLastNonZeroGainPct = (pct: number) => {
        if (!clip?.id || pct <= 0) return;
        const key = isScriptClip(clip)
            ? `${clip.id}:scene:${String(selectedSceneIndex ?? playheadSceneIndex ?? "clip")}`
            : String(clip.id);
        lastNonZeroGainPctByKeyRef.current[key] = pct;
    };

    const previewGain = (pct: number) => {
        const clampedPct = clampVolumePct(pct);
        setGainPct(clampedPct);
        rememberLastNonZeroGainPct(clampedPct);
    };

    const commitGain = (pct: number) => {
        if (!clip) return;
        const clampedPct = clampVolumePct(pct);
        previewGain(clampedPct);
        // Map 0..400% → 0..4 linear gain. Keep 100% as 1.0.
        const linear = clampedPct / 100;

        // For script clips, apply to scene-specific properties
        // Use selectedSceneIndex if explicitly selected, otherwise use playheadSceneIndex
        if (isScriptClip(clip)) {
            const effectiveSceneIndex = selectedSceneIndex ?? playheadSceneIndex;
            if (effectiveSceneIndex !== null) {
                // Set scene-specific gain
                clip.setSceneProperties(effectiveSceneIndex, { gain: linear });
                if (clip.id) {
                    // Keep waveform scale indicator in sync without forcing full waveform recompute.
                    queueDeferredWaveformGainSync(clip.id, linear);
                }
                queueDeferredClipUpdated(clip);
                return;
            }
        }

        if (isAudioClip(clip) && typeof clip.volume === 'number') {
            // AudioClip exposes live volume setter that propagates to mixer
            clip.volume = linear;
        } else if (isMedia) {
            clip.gain = linear;
            // Recompile schedule asynchronously so commit doesn't block pointer interactions.
            queueDeferredTimingChanged();
        }
        // Waveform-store gain updates are deferred so dragging stays smooth.
        if (clip.id) {
            queueDeferredWaveformGainSync(clip.id, linear);
        }
        queueDeferredClipUpdated(clip);
    };

    const scheduleCommittedGain = (pct: number) => {
        const clampedPct = clampVolumePct(pct);
        previewGain(clampedPct);
        cancelDeferredGainCommit();
        deferredGainCommitTimerRef.current = window.setTimeout(() => {
            deferredGainCommitTimerRef.current = null;
            commitGain(clampedPct);
        }, 90);
    };

    const toggleClipMute = () => {
        if (!clip) return;
        cancelDeferredGainCommit();
        cancelDeferredGainFollowUpWork();
        const key = isScriptClip(clip)
            ? `${clip.id}:scene:${String(selectedSceneIndex ?? playheadSceneIndex ?? "clip")}`
            : String(clip.id);
        const isMuted = gainPct <= 0;
        if (isMuted) {
            const restore = Number(lastNonZeroGainPctByKeyRef.current[key] ?? 100);
            commitGain(restore > 0 ? restore : 100);
        } else {
            lastNonZeroGainPctByKeyRef.current[key] = gainPct > 0 ? gainPct : 100;
            commitGain(0);
        }
    };

    const applyOpacity = (pct: number) => {
        if (!clip || !hasVisualElement) return;
        setOpacityPct(pct);
        const alpha = Math.max(0, Math.min(100, pct)) / 100;
        applyVisualProp({ alpha });
    };

    const durationLabel = (() => {
        if (!clip) return '--:--';
        try {
            // Prefer timelineDuration when available (MediaClip), else stop-start
            const secs =
                typeof clip.timelineDuration?.seconds === 'number'
                    ? clip.timelineDuration.seconds
                    : Math.max(0, (clip.stop?.seconds ?? 0) - (clip.start?.seconds ?? 0));
            return formatDurationBrief(secs);
        } catch {
            return '--:--';
        }
    })();

    // Get track metadata for enhanced state (Studio Sound)
    // States: "" (not requested), "queued", "processing", "failed", "ready"
    const getTrackEnhancedState = (): {
        state: string;
        isReady: boolean;
        isQueued: boolean;
        isProcessing: boolean;
        isFailed: boolean;
        isAvailable: boolean;
        isSilent: boolean;
    } => {
        if (!clip) {
            return { state: '', isReady: false, isQueued: false, isProcessing: false, isFailed: false, isAvailable: false, isSilent: false };
        }

        // Handle ScriptLayerClip (multiple audio sources)
        // Studio Sound is only "ready" when ALL audio sources have denoised audio ready
        if (isScriptClip(clip)) {
            const audioEntries = clip.getAudioEntries?.() ?? [];
            if (audioEntries.length === 0) {
                return { state: '', isReady: false, isQueued: false, isProcessing: false, isFailed: false, isAvailable: false, isSilent: false };
            }

            let allNonSilentReady = true;
            let anyQueued = false;
            let anyProcessing = false;
            let anyFailed = false;
            let anyAvailable = false;
            let anyNonSilent = false;
            let anySilent = false;

            for (const entry of audioEntries) {
                const track = findTrackRecord(entry.assetId);
                const silent = isTrackSilent(track);
                if (silent) {
                    anySilent = true;
                    anyAvailable = true;
                    continue;
                }
                anyNonSilent = true;
                const state = normalizeState(track?.enhancedTrackState);
                if (state !== 'ready') allNonSilentReady = false;
                if (state === 'queued') anyQueued = true;
                if (state === 'processing') anyProcessing = true;
                if (state === 'failed') anyFailed = true;
                if (state !== '') anyAvailable = true;
            }

            const isReady = anyNonSilent ? allNonSilentReady : true;
            const isProcessing = anyNonSilent && !isReady && anyProcessing;
            const isQueued = anyNonSilent && !isReady && !anyProcessing && anyQueued;
            const isFailed = anyNonSilent && !isReady && !anyProcessing && !anyQueued && anyFailed;
            const isSilent = !anyNonSilent && anySilent;
            const state = isSilent
                ? 'silent'
                : isReady
                    ? 'ready'
                    : isProcessing
                        ? 'processing'
                        : isQueued
                            ? 'queued'
                        : isFailed
                            ? 'failed'
                            : '';

            return {
                state,
                isReady,
                isQueued,
                isProcessing,
                isFailed,
                isAvailable: anyAvailable || anyNonSilent || anySilent,
                isSilent,
            };
        }

        // Handle AudioClip/VideoClip (single audio source)
        const trackId = CompositionStateStore.clipIdToAssetId?.[clip.id];
        if (!trackId) return { state: '', isReady: false, isQueued: false, isProcessing: false, isFailed: false, isAvailable: true, isSilent: false };

        // Find track in ProjectAssetsStore
        const track = findTrackRecord(trackId);
        if (!track) return { state: '', isReady: false, isQueued: false, isProcessing: false, isFailed: false, isAvailable: true, isSilent: false };

        const state = normalizeState(track.enhancedTrackState);
        const silent = isTrackSilent(track);
        return {
            state: state || (silent ? 'silent' : ''),
            isReady: state === 'ready' || silent,
            isQueued: state === 'queued',
            isProcessing: state === 'processing',
            isFailed: state === 'failed',
            isAvailable: true,
            isSilent: silent,
        };
    };

    const enhancedState = getTrackEnhancedState();
    const showStudioSoundSpinner =
        studioSoundSwitching ||
        (studioSound && (studioSoundPendingEnable || enhancedState.isQueued || enhancedState.isProcessing));

    const getOriginalTrackUrl = async (cacheKey: string, trackId: string): Promise<string | undefined> => {
        let originalUrl = originalAudioUrlsRef.current.get(cacheKey);
        if (!originalUrl) {
            originalUrl = await GetTrackUrl({ trackId });
            if (originalUrl) {
                originalAudioUrlsRef.current.set(cacheKey, originalUrl);
            }
        }
        return originalUrl;
    };

    const markTrackEnhancementQueuedOptimistic = (trackId: string) => {
        try {
            ProjectAssetsStore.upsertOptimisticTrack({
                id: trackId,
                trackId,
                enhancedTrackState: 'queued',
            });
        } catch {}
    };

    const requestTrackEnhancement = async (trackId: string, currentState: string): Promise<boolean> => {
        const normalizedState = normalizeState(currentState);
        if (normalizedState === 'processing' || normalizedState === 'queued') return true;
        const ok = await TriggerEnhance({ trackId });
        if (ok) {
            markTrackEnhancementQueuedOptimistic(trackId);
            return true;
        }
        return false;
    };

    // Handle Studio Sound toggle
    const handleStudioSoundToggle = async (
        enabled: boolean,
        opts?: { fromAutoApply?: boolean }
    ) => {
        if (studioSoundSwitching || !clip) return;
        if (!opts?.fromAutoApply) {
            trackSessionEvent("studio_sound_toggled");
        }

        // Minimum delay when enabling Studio Sound to make it feel more premium
        const MIN_DELAY_MS = enabled ? 1000 : 0;
        const startTime = Date.now();
        const wasPlayingAtToggleStart = CompositionStateStore.isPlaying === true;
        const resumeTimeSec = Number(CompositionStateStore.currentTime ?? 0);

        const reprimePlaybackAfterToggleIfNeeded = async () => {
            if (!wasPlayingAtToggleStart) return;
            try {
                await CompositionService.pause();
                await new Promise((resolve) => setTimeout(resolve, 60));
                if (Number.isFinite(resumeTimeSec) && resumeTimeSec >= 0) {
                    await CompositionService.seek(resumeTimeSec);
                }
                await CompositionService.play();
            } catch (e) {
                console.warn('[StudioSound] Failed to re-prime playback after toggle:', e);
            }
        };

        setStudioSoundSwitching(true);
        setStudioSound(enabled);
        if (!enabled) setStudioSoundPendingEnable(false);

        try {
            // Handle ScriptLayerClip (multiple audio sources)
            if (isScriptClip(clip)) {
                const audioEntries = clip.getAudioEntries?.() ?? [];

                if (audioEntries.length === 0) {
                    console.warn('[StudioSound] ScriptLayerClip has no audio entries');
                    setStudioSound(false);
                    return;
                }

                // Build Maps of assetId -> URL for all audio sources
                const enhancedUrls = new Map<string, string>();
                const originalUrls = new Map<string, string>();
                const nonSilentAssetIds: string[] = [];
                const trackStateByAssetId = new Map<string, string>();

                for (const entry of audioEntries) {
                    const assetId = entry.assetId;
                    if (!assetId) continue;
                    const trackRecord = findTrackRecord(assetId);
                    const isSilent = isTrackSilent(trackRecord);
                    if (!isSilent) nonSilentAssetIds.push(assetId);
                    trackStateByAssetId.set(assetId, normalizeState(trackRecord?.enhancedTrackState));

                    // Get/store original URL
                    const originalUrl = await getOriginalTrackUrl(assetId, assetId);
                    if (originalUrl) {
                        originalUrls.set(assetId, originalUrl);
                    }

                    // Get enhanced URL if enabling (silent sources use original as a no-op)
                    if (enabled) {
                        if (isSilent) {
                            if (originalUrl) enhancedUrls.set(assetId, originalUrl);
                        } else {
                            const enhancedUrl = await GetEnhancedAudioUrl({ trackId: assetId });
                            if (enhancedUrl) {
                                enhancedUrls.set(assetId, enhancedUrl);
                            }
                        }
                    }
                }

                if (enabled) {
                    if (nonSilentAssetIds.length > 0) {
                        const missing = nonSilentAssetIds.filter((id) => !enhancedUrls.has(id));
                        if (missing.length > 0) {
                            setStudioSoundPendingEnable(true);

                            const requestResults = await Promise.all(
                                missing.map(async (assetId) => {
                                    const state = trackStateByAssetId.get(assetId) ?? '';
                                    return requestTrackEnhancement(assetId, state);
                                })
                            );

                            if (!requestResults.some(Boolean)) {
                                setStudioSoundPendingEnable(false);
                                setStudioSound(false);
                            }
                            return;
                        }
                    }
                    setStudioSoundPendingEnable(false);
                }

                // Wait for minimum delay if needed
                const elapsed = Date.now() - startTime;
                if (elapsed < MIN_DELAY_MS) {
                    await new Promise(resolve => setTimeout(resolve, MIN_DELAY_MS - elapsed));
                }

                await (clip as any).setStudioSound(enabled, enhancedUrls, originalUrls);
                try { CompositionService.notifyClipUpdated(clip); } catch {}
                await reprimePlaybackAfterToggleIfNeeded();
                return;
            }

            // Handle AudioClip/VideoClip (single audio source)
            const trackId = CompositionStateStore.clipIdToAssetId?.[clip.id];
            if (!trackId) {
                console.warn('[StudioSound] No trackId found for clip');
                setStudioSound(false);
                return;
            }

            const currentState = enhancedState.state;
            const trackRecord = findTrackRecord(trackId);
            const isSilent = isTrackSilent(trackRecord);

            // Get/store original URL
            const originalUrl = await getOriginalTrackUrl(trackId, trackId);

            // Get enhanced URL (for enabling) - silent sources use original as a no-op
            const enhancedUrl = enabled
                ? isSilent
                    ? originalUrl
                    : await GetEnhancedAudioUrl({ trackId })
                : undefined;

            if (enabled && !enhancedUrl) {
                const waiting = await requestTrackEnhancement(trackId, currentState);
                setStudioSoundPendingEnable(waiting);
                if (!waiting) setStudioSound(false);
                return;
            }

            // Wait for minimum delay if needed (makes Studio Sound feel more premium)
            const elapsed = Date.now() - startTime;
            if (elapsed < MIN_DELAY_MS) {
                await new Promise(resolve => setTimeout(resolve, MIN_DELAY_MS - elapsed));
            }

            // Use setStudioSound which handles source swap AND emits event for waveform update
            if (isAudioClip(clip) || isVideoClip(clip)) {
                if (enabled) setStudioSoundPendingEnable(false);
                await (clip as any).setStudioSound(enabled, enhancedUrl, originalUrl);
                try { CompositionService.notifyClipUpdated(clip); } catch {}
                await reprimePlaybackAfterToggleIfNeeded();
            }
        } catch (error) {
            console.error('[StudioSound] Failed to toggle:', error);
            setStudioSound(!enabled); // Revert on error
            if (enabled) setStudioSoundPendingEnable(false);
        } finally {
            setStudioSoundSwitching(false);
        }
    };

    // If user has requested Studio Sound and enhancement becomes ready, auto-apply without requiring another toggle.
    useEffect(() => {
        if (!clip) return;
        if (!studioSound) return;
        if (!studioSoundPendingEnable) return;
        if (!enhancedState.isReady) return;
        if (studioSoundSwitching) return;
        void handleStudioSoundToggle(true, { fromAutoApply: true });
    }, [
        clip?.id,
        studioSound,
        studioSoundPendingEnable,
        enhancedState.isReady,
        enhancedState.state,
        studioSoundSwitching,
    ]);

	    if (!hasClip) {
	        const effectiveSceneIndex =
	            typeof selectedSceneIndex === "number" && Number.isFinite(selectedSceneIndex)
	                ? selectedSceneIndex
	                : playheadSceneIndex;
	
	        const applyLayoutFromPicker = (layoutRef: string) => {
	            const pending = ApplicationStore.consumePendingAddSceneFromSelectionContext();
	            if (pending) {
	                const EPS = 1e-3;
	                const startSec = Math.max(0, Number(pending.startSec ?? 0));
	                const endSec = Math.max(startSec, Number(pending.endSec ?? 0));
	                if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + EPS) {
	                    // Fall back to applying to the current scene.
	                } else {
	                    try {
	                        CompositionService.splitScriptLayer(startSec, true);
	                    } catch {}
	                    try {
	                        CompositionService.splitScriptLayer(endSec, true);
	                    } catch {}
	
	                    let targetSceneIndex: number | null = null;
	                    try {
	                        const clip: any = CompositionService.getScriptLayerClip();
	                        if (clip && typeof clip.findSceneIndexForTime === "function") {
	                            const sampleStart = Math.min(startSec + EPS, endSec);
	                            const sampleEnd = Math.max(sampleStart, endSec - EPS);
	                            let startIdx = Number(clip.findSceneIndexForTime(sampleStart));
	                            let endIdx = Number(clip.findSceneIndexForTime(sampleEnd));
	                            if (Number.isFinite(startIdx) && Number.isFinite(endIdx) && startIdx >= 0 && endIdx >= 0) {
	                                // Merge any existing scene boundaries inside the selection so it becomes a single scene.
	                                while (endIdx > startIdx) {
	                                    CompositionService.mergeScriptLayerSceneWithPrevious(startIdx + 1);
	                                    endIdx--;
	                                }
	                            }
	
	                            const clipAfter: any = CompositionService.getScriptLayerClip();
	                            if (clipAfter && typeof clipAfter.findSceneIndexForTime === "function") {
	                                const idx = Number(clipAfter.findSceneIndexForTime(sampleStart));
	                                if (Number.isFinite(idx) && idx >= 0) targetSceneIndex = idx;
	                            }
	
	                            if (targetSceneIndex != null && clipAfter && typeof clipAfter.getScenes === "function") {
	                                const scenes = clipAfter.getScenes() as any[];
	                                const scene =
	                                    scenes.find((s: any) => Number(s?.index) === targetSceneIndex) ?? scenes[targetSceneIndex];
	                                if (scene && Number.isFinite(Number(scene.startSec)) && Number.isFinite(Number(scene.endSec))) {
	                                    try {
	                                        ApplicationStore.setSelectedSceneIndex(targetSceneIndex, {
	                                            start: Number(scene.startSec),
	                                            end: Number(scene.endSec),
	                                        });
	                                    } catch {}
	                                } else {
	                                    try {
	                                        ApplicationStore.setSelectedSceneIndex(targetSceneIndex);
	                                    } catch {}
	                                }
	                            }
	                        }
	                    } catch {}
	
	                    if (targetSceneIndex != null) {
	                        try {
	                            LayoutService.applySceneLayout(targetSceneIndex, layoutRef);
	                        } catch {}
	                        setSceneLayout(layoutRef);
	                        return;
	                    }
	                }
	            }
	
	            if (effectiveSceneIndex !== null) {
	                try {
	                    LayoutService.applySceneLayout(effectiveSceneIndex, layoutRef);
	                } catch {}
	            }
	            setSceneLayout(layoutRef);
	        };
	        return (
	            <>
		                <PaperPanel title="Layout">
		                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, p: 1}}>
		                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
	                            <Box
	                                data-tour="layout-picker-button"
	                                ref={layoutPickerButtonRef}
	                                onClick={(e: any) => {
	                                    setLayoutAnchorEl((layoutPickerButtonRef.current as any) ?? (e.currentTarget as HTMLElement));
                                    setLayoutOpen(o => !o);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    height: 36,
                                    px: 1,
                                    borderRadius: '10px',
                                    border: '1px solid var(--app-border-subtle)',
                                    background: 'var(--app-bg-subtle)',
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }}
                            >
                                <Typography sx={{fontSize: 13, color: sceneLayout ? 'var(--app-text-primary)' : 'var(--app-text-muted)'}}>
                                    {getLayoutDisplayName(sceneLayout) ?? "Choose layout"}
                                </Typography>
                                <Box sx={{fontSize: 12, color: 'var(--app-text-muted)'}}>▼</Box>
                            </Box>

			                            <Popper open={layoutOpen} anchorEl={layoutAnchorEl} placement="bottom-start"
			                                    sx={{zIndex: 1300}}>
			                                <ClickAwayListener onClickAway={() => {
	                                            setLayoutOpen(false);
	                                            setMulticamMoreOpen(false);
	                                            // Cancel any pending "Add Scene from selection" flow if the user dismisses the picker.
	                                            try { ApplicationStore.setPendingAddSceneFromSelectionContext(null); } catch {}
	                                        }}>
			                                    <Paper elevation={3} sx={{p: 1, borderRadius: '12px', width: 295}}>
		                                        <Box sx={{
		                                            display: 'grid',
		                                            gridTemplateColumns: 'repeat(2, 1fr)',
		                                            gap: 1.25,
	                                            justifyItems: 'center'
	                                        }}>
			                                            {layoutOptions.map(opt => (
			                                                <LayoutOption
			                                                    key={opt.layoutId}
			                                                    name={opt.name}
			                                                    selected={isLayoutSelectedForOption(sceneLayout, opt.layoutId, activeLayoutPackId)}
			                                                    previewUrl={opt.previewUrl}
			                                                    posterUrl={opt.posterUrl}
			                                                    previewObjectFit={opt.previewObjectFit}
                                                            dataTour={opt.layoutId === "multicam" ? "layout-option-multicam" : undefined}
	                                                            moreButton={
	                                                                opt.layoutId === "multicam"
	                                                                    ? {
                                                                          label: "More",
                                                                          onClick: (e) => {
                                                                              setMulticamMoreAnchorEl(e.currentTarget);
                                                                              setMulticamMoreOpen(true);
                                                                          },
                                                                      }
                                                                    : undefined
                                                            }
			                                                    onClick={() => {
			                                                        const pack = LayoutPacksStore.getPack(activeLayoutPackId) ?? BUILTIN_LAYOUT_PACK;
			                                                        const defaultKey =
			                                                            String(pack.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "").trim() ||
			                                                            String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "two");
			                                                        const variantKey = String(multicamVariantKeyByPreset[canvasAspectPreset] ?? defaultKey);
			                                                        const layoutIdToApply =
			                                                            opt.layoutId === "multicam" ? `multicam:${variantKey}` : opt.layoutId;
			                                                        const nextLayoutRef = formatLayoutRef(pack.id, normalizeLayoutId(layoutIdToApply));
			                                                        applyLayoutFromPicker(nextLayoutRef);
			                                                                setMulticamMoreOpen(false);
			                                                        setLayoutOpen(false);
			                                                    }}
			                                                />
			                                            ))}
		                                        </Box>
                                                <Popper
                                                    open={multicamMoreOpen}
                                                    anchorEl={multicamMoreAnchorEl}
                                                    placement="left-start"
                                                    disablePortal
                                                    sx={{ zIndex: 1400 }}
                                                >
                                                    <ClickAwayListener onClickAway={() => setMulticamMoreOpen(false)}>
                                                        <Paper
                                                            elevation={4}
                                                            sx={{
                                                                p: 1,
                                                                borderRadius: '12px',
                                                                width: 310,
                                                                maxHeight: 420,
                                                                overflowY: 'auto',
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, mb: 0.75 }}>
                                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--app-text-primary)' }}>
                                                                    Multicam
                                                                </Typography>
                                                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setMulticamMoreOpen(false)}>
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                                    gap: 1.25,
                                                                    justifyItems: 'center',
                                                                }}
                                                            >
                                                                {(() => {
                                                                    const pack = LayoutPacksStore.getPack(activeLayoutPackId) ?? BUILTIN_LAYOUT_PACK;
                                                                    const variants = (pack.multicamVariantsByPreset?.[canvasAspectPreset] ??
                                                                        BUILTIN_LAYOUT_PACK.multicamVariantsByPreset?.[canvasAspectPreset] ??
                                                                        []) as MulticamVariant[];
                                                                    const defaultKey =
                                                                        String(pack.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "").trim() ||
                                                                        String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.[canvasAspectPreset] ?? "two");
                                                                    return variants.map((variant) => {
                                                                const isSelected =
                                                                    String(multicamVariantKeyByPreset[canvasAspectPreset] ?? defaultKey) === variant.key;
                                                                const urls = previewToUrls(variant as any, canvasAspectPreset);
                                                                const objectFit =
                                                                    canvasAspectPreset === "horizontal" ? "cover" : ("contain" as const);

                                                                return (
                                                                    <LayoutOption
                                                                                key={variant.key}
                                                                                name={variant.label}
                                                                                selected={isSelected}
                                                                                previewUrl={urls.previewUrl}
                                                                                posterUrl={urls.posterUrl}
                                                                                previewObjectFit={objectFit}
	                                                                                onClick={() => {
	                                                                                    const nextLayoutRef = formatLayoutRef(
	                                                                                        pack.id,
	                                                                                        normalizeLayoutId(`multicam:${variant.key}`)
	                                                                                    );
	                                                                                    setMulticamVariantKeyByPreset((prev) => ({
	                                                                                        ...prev,
	                                                                                        [canvasAspectPreset]: variant.key,
	                                                                                    }));
	
	                                                                                    applyLayoutFromPicker(nextLayoutRef);
	                                                                                    setMulticamMoreOpen(false);
	                                                                                    setLayoutOpen(false);
	                                                                                }}
	                                                                            />
	                                                                        );
                                                                    });
                                                                })()}
                                                            </Box>
                                                        </Paper>
                                                    </ClickAwayListener>
                                                </Popper>
		                                        {/*<Box sx={{justifySelf: 'center', mt: 2, mb: 1}}>*/}
		                                        {/*    <ChangeLayoutPackButton disabled={true} onClick={() => {}}/>*/}
		                                        {/*</Box>*/}
		                                    </Paper>
		                                </ClickAwayListener>
                            </Popper>
                        </Box>
                    </Box>
                </PaperPanel>

                <PaperPanel title="Background">
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, p: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Box
                                component="input"
                                type="color"
                                value={sceneBackgroundColor}
                                onChange={(e: any) => {
                                    const v = String(e?.target?.value ?? "").trim();
                                    if (!/^#[0-9a-fA-F]{6}$/.test(v)) return;
                                    setSceneBackgroundColor(v);
                                    CompositionService.setBackgroundColor(v as any);
                                }}
                                sx={{
                                    width: 28, height: 28, p: 0, border: '1px solid var(--app-border-subtle)',
                                    borderRadius: '6px', cursor: 'pointer', background: 'transparent'
                                }}
                            />
                            <Typography sx={{fontSize: 12, color: 'var(--app-text-muted)'}}>Canvas color</Typography>
                        </Box>
                    </Box>
                </PaperPanel>

                <PaperPanel
                    title="Transitions"
                    showDivider={hasTransitions}
                    headerContent={
                        <IconButton
                            size="small"
                            onClick={openTransitionMenu}
                            sx={{ color: "var(--app-text-muted)", p: 0.5 }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    {hasTransitions && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 0.5, py: 0.5 }}>
                            {sceneTransitions.in && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        border: "1px solid var(--app-border-subtle)",
                                        borderRadius: "8px",
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "var(--app-text-muted)", width: 24 }}>
                                        IN
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={sceneTransitions.in.type}
                                        onChange={(e) =>
                                            handleTransitionTypeChange("in", String(e.target.value) as TransitionType)
                                        }
                                        MenuProps={transitionSelectMenuProps}
                                        sx={transitionSelectSx}
                                    >
                                        {TRANSITION_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value} sx={transitionMenuItemSx}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <IconButton
                                        size="small"
                                        sx={{ color: "var(--app-text-muted)", p: 0.25 }}
                                        title="Transition controls"
                                        onClick={(event) => openTransitionControls("in", event)}
                                    >
                                        <TuneIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        sx={{ color: "var(--app-text-muted)", p: 0.25 }}
                                        onClick={() => handleRemoveTransition("in")}
                                        title="Remove transition"
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            {sceneTransitions.out && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        border: "1px solid var(--app-border-subtle)",
                                        borderRadius: "8px",
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "var(--app-text-muted)", width: 24 }}>
                                        OUT
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={sceneTransitions.out.type}
                                        onChange={(e) =>
                                            handleTransitionTypeChange("out", String(e.target.value) as TransitionType)
                                        }
                                        MenuProps={transitionSelectMenuProps}
                                        sx={transitionSelectSx}
                                    >
                                        {TRANSITION_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value} sx={transitionMenuItemSx}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <IconButton
                                        size="small"
                                        sx={{ color: "var(--app-text-muted)", p: 0.25 }}
                                        title="Transition controls"
                                        onClick={(event) => openTransitionControls("out", event)}
                                    >
                                        <TuneIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        sx={{ color: "var(--app-text-muted)", p: 0.25 }}
                                        onClick={() => handleRemoveTransition("out")}
                                        title="Remove transition"
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    )}
                </PaperPanel>

                <Popper open={transitionMenuOpen} anchorEl={transitionMenuAnchorEl} placement="bottom-end" sx={{ zIndex: 1300 }}>
                    <ClickAwayListener onClickAway={closeTransitionMenu}>
                        <Paper elevation={3} sx={{ p: 0.5, borderRadius: "8px", minWidth: 110 }}>
                            <Box
                                sx={{ display: "flex", flexDirection: "column" }}
                                onMouseLeave={() => {
                                    if (!transitionSubmenuHover) setTransitionMenuHover(null);
                                }}
                            >
                                <Box
                                    ref={transitionInRef}
                                    onMouseEnter={() => setTransitionMenuHover("in")}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13, color: "var(--app-text-primary)" }}>In</Typography>
                                    <KeyboardArrowRightIcon sx={{ fontSize: 18, color: "var(--app-text-muted)" }} />
                                </Box>
                                <Box
                                    ref={transitionOutRef}
                                    onMouseEnter={() => setTransitionMenuHover("out")}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13, color: "var(--app-text-primary)" }}>Out</Typography>
                                    <KeyboardArrowRightIcon sx={{ fontSize: 18, color: "var(--app-text-muted)" }} />
                                </Box>
                            </Box>
                        </Paper>
                    </ClickAwayListener>
                </Popper>

                <Popper
                    open={Boolean(transitionMenuHover) && Boolean(transitionMenuHover === "in" ? transitionInRef.current : transitionOutRef.current)}
                    anchorEl={transitionMenuHover === "in" ? transitionInRef.current : transitionOutRef.current}
                    placement="left-start"
                    sx={{ zIndex: 1301 }}
                >
                    <Paper
                        elevation={3}
                        sx={{ p: 0.5, borderRadius: "8px", minWidth: 180 }}
                        onMouseEnter={() => setTransitionSubmenuHover(true)}
                        onMouseLeave={() => {
                            setTransitionSubmenuHover(false);
                            setTransitionMenuHover(null);
                        }}
                    >
                        {TRANSITION_OPTIONS.map((opt) => (
                            <Box
                                key={opt.value}
                                onClick={() =>
                                    transitionMenuHover &&
                                    handleSelectTransition(transitionMenuHover, opt.value as TransitionType)
                                }
                                sx={{
                                    ...transitionMenuItemSx,
                                    cursor: "pointer",
                                }}
                            >
                                <Typography sx={{ fontSize: 13, color: "var(--app-text-primary)" }}>{opt.label}</Typography>
                            </Box>
                        ))}
                    </Paper>
                </Popper>

                <Popper
                    open={Boolean(transitionControlsAnchorEl && transitionControlsEdge && activeTransition)}
                    anchorEl={transitionControlsAnchorEl}
                    placement="bottom-start"
                    sx={{ zIndex: 1302 }}
                >
                    <ClickAwayListener onClickAway={closeTransitionControls}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                minWidth: 240,
                                boxShadow: "0 18px 40px rgba(0,0,0,0.16)",
                                border: "1px solid rgba(15, 15, 15, 0.06)",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--app-text-primary)" }}>
                                    {transitionControlsEdge === "in" ? "Transition in" : "Transition out"}
                                </Typography>
                                <IconButton size="small" onClick={closeTransitionControls} sx={{ p: 0.25, color: "var(--app-text-muted)" }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            {activeTransition && transitionControlsEdge && (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "var(--app-text-muted)", width: 24 }}>
                                            {transitionControlsEdge.toUpperCase()}
                                        </Typography>
                                        <Select
                                            size="small"
                                            value={activeTransition.type}
                                            onChange={(e) =>
                                                handleTransitionTypeChange(
                                                    transitionControlsEdge,
                                                    String(e.target.value) as TransitionType
                                                )
                                            }
                                            MenuProps={transitionSelectMenuProps}
                                            sx={transitionSelectSx}
                                        >
                                            {TRANSITION_OPTIONS.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value} sx={transitionMenuItemSx}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>

                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>Duration</Typography>
                                        <TextField
                                            size="small"
                                            value={activeTransition.duration}
                                            onChange={(e) =>
                                                updateTransitionDuration(transitionControlsEdge, e.target.value, false)
                                            }
                                            onBlur={() =>
                                                updateTransitionDuration(
                                                    transitionControlsEdge,
                                                    activeTransition.duration,
                                                    true
                                                )
                                            }
                                            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.]?[0-9]?" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AccessTimeIcon sx={{ fontSize: 16, color: "var(--app-text-subtle)" }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Typography sx={{ fontSize: 12, color: "var(--app-text-subtle)" }}>s</Typography>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                width: 96,
                                                "& .MuiInputBase-input": { fontSize: 13, py: 0.5, textAlign: "right" },
                                            }}
                                        />
                                    </Box>

                                    {activeTransition.type === "smart" && (
                                        <>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>Easing</Typography>
                                                <Select
                                                    size="small"
                                                    value={activeTransition.easing}
                                                    onChange={(e) =>
                                                        updateTransitionEasing(transitionControlsEdge, String(e.target.value))
                                                    }
                                                    MenuProps={transitionSelectMenuProps}
                                                    sx={{ ...transitionSelectSx, width: 140 }}
                                                >
                                                    {EASING_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value} sx={transitionMenuItemSx}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>Add fades</Typography>
                                                <Switch
                                                    size="small"
                                                    checked={activeTransition.addFades}
                                                    onChange={(_, checked) =>
                                                        updateTransitionAddFades(transitionControlsEdge, checked)
                                                    }
                                                />
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </ClickAwayListener>
                </Popper>

                <LayersPanel playheadSceneIndex={playheadSceneIndex} />
            </>
        );
    }

	    return (
	        <>
	            {shouldShowSceneScopeToggle && (
	                <Box sx={{ px: 1, py: 0.5 }}>
	                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
	                        <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>Selected in</Typography>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                padding: "3px",
                                borderRadius: "10px",
                                backgroundColor: "var(--app-bg-subtle)",
                                border: "1px solid var(--app-border-subtle)",
                            }}
                        >
                            <Box
                                component="button"
                                onClick={() => ApplicationStore.setSceneEditScope("scene")}
                                sx={{
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    fontWeight: 500,
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    backgroundColor: sceneEditScope === "scene" ? "var(--app-bg-surface)" : "transparent",
                                    color: sceneEditScope === "scene" ? "var(--app-text-primary)" : "var(--app-text-muted)",
                                    boxShadow: sceneEditScope === "scene" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                    userSelect: "none",
                                }}
                            >
                                Current scene
                            </Box>
                            <Box
                                component="button"
                                onClick={() => ApplicationStore.setSceneEditScope("all")}
                                sx={{
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    fontWeight: 500,
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    backgroundColor: sceneEditScope === "all" ? "var(--app-bg-surface)" : "transparent",
                                    color: sceneEditScope === "all" ? "var(--app-text-primary)" : "var(--app-text-muted)",
                                    boxShadow: sceneEditScope === "all" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                    userSelect: "none",
                                }}
                            >
                                All scenes
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Duration (Audio/Video) */}
            {isMedia && (
                <PaperPanel title={isEditingScene ? "Speed" : "Duration"}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, px: 0.5, py: 0.5}}>
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                            <ReplayRoundedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                            <Typography sx={{fontSize: 12, color: 'text.secondary', width: 52}}>Speed</Typography>
                            <Select
                                size="small"
                                value={rate}
                                onChange={(e) => applyPlaybackRate(Number(e.target.value))}
                                sx={{minWidth: 100, '& .MuiSelect-select': {py: 0.5, fontSize: 13}}}
                            >
                                {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4].map(r => (
                                    <MenuItem key={r} value={r}>{r}x</MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                            <AccessTimeIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                            <Typography sx={{fontSize: 12, color: 'text.secondary', width: 52}}>Length</Typography>
                            <TextField
                                size="small"
                                aria-readonly={true}
                                disabled={true}
                                value={durationLabel}
                                InputProps={{readOnly: true}}
                                sx={{'& .MuiInputBase-input': {fontSize: 13, py: 0.5,}, width: 100}}
                            />
                        </Box>
                    </Box>
                </PaperPanel>
            )}

            {/* Audio (Audio clips and Video with audio) */}
            {hasAudioStream && (
                <PaperPanel title="Audio">
                    <DeferredVolumeControl
                        committedPct={gainPct}
                        onToggleMute={toggleClipMute}
                        onDragStart={() => {
                            cancelDeferredGainCommit();
                            cancelDeferredGainFollowUpWork();
                        }}
                        onCommit={scheduleCommittedGain}
                    />
                </PaperPanel>
            )}

            {/* Opacity (Video/Image/ScriptLayerClip visual sources) */}
            {hasVisualElement && (
                <PaperPanel title="Opacity">
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 1}}>
                        <VisibilityRoundedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
                        <Typography sx={{fontSize: 12, color: 'text.secondary', width: 52}}>Opacity</Typography>
                        <Slider
                            size="small"
                            value={opacityPct}
                            onChange={(_, v) => applyOpacity(Array.isArray(v) ? v[0] : v)}
                            onChangeCommitted={() => {
                                try { if (clip) CompositionService.notifyClipUpdated(clip); } catch {}
                            }}
                            min={0}
                            max={100}
                            sx={{flex: 1, color: 'text.primary'}}
                        />
                        <Box sx={{width: 48, textAlign: 'right', fontSize: 12, color: 'text.secondary'}}>
                            {opacityPct}%
                        </Box>
                    </Box>
                </PaperPanel>
            )}

	            {/* Size & Position (Video/Image/ScriptLayerClip visual sources) */}
	            {hasVisualElement && (
	                <PaperPanel title="Size and position">
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, px: 0.5, py: 1}}>
                        <TextField
                            size="small"
                            label="X"
                            value={posX}
                            onChange={(e) => {
                                const v = e.target.value.trim();
                                setPosX(v);
                            }}
                            onBlur={commitPosX}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    commitPosX();
                                }
                            }}
                            sx={{'& .MuiInputBase-input': {fontSize: 13, py: 0.5}, width: 104}}
                        />
                        <TextField
                            size="small"
                            label="Y"
                            value={posY}
                            onChange={(e) => {
                                const v = e.target.value.trim();
                                setPosY(v);
                            }}
                            onBlur={commitPosY}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    commitPosY();
                                }
                            }}
                            sx={{'& .MuiInputBase-input': {fontSize: 13, py: 0.5}, width: 104}}
                        />
                        <TextField
                            size="small"
                            label="W"
                            value={wStr}
                            onChange={(e) => {
                                const v = e.target.value.trim();
                                setWStr(v);
                            }}
                            onBlur={commitWidth}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    commitWidth();
                                }
                            }}
                            sx={{'& .MuiInputBase-input': {fontSize: 13, py: 0.5}, width: 104}}
                        />
                        <TextField
                            size="small"
                            label="H"
                            value={hStr}
                            onChange={(e) => {
                                const v = e.target.value.trim();
                                setHStr(v);
                            }}
                            onBlur={commitHeight}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    commitHeight();
                                }
                            }}
                            sx={{'& .MuiInputBase-input': {fontSize: 13, py: 0.5}, width: 104}}
                        />
                    </Box>
                </PaperPanel>
            )}

	            {/* (Launch) Animation panel disabled for now */}
	            {false && supportsClipAnimations && (
	                <PaperPanel
	                    title="Animation"
	                    showDivider={clipAnimations.length > 0}
	                    headerContent={
                        <IconButton
                            size="small"
                            onClick={(event) => setAnimationMenuAnchorEl(event.currentTarget)}
                            sx={{ color: "var(--app-text-muted)", p: 0.5 }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    {clipAnimations.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 0.5, py: 0.5 }}>
                            {clipAnimations.map((animation) => (
                                <Box
                                    key={animation.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        border: "1px solid var(--app-border-subtle)",
                                        borderRadius: "10px",
                                        px: 1,
                                        py: 0.75,
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)", lineHeight: 1.25 }}>
                                            {animation.kind === "zoom_pan" ? "Zoom and Pan" : "Custom Animation"}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", lineHeight: 1.35 }}>
                                            {formatAnimationTimestamp(animation.startSec)}
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        size="small"
                                        sx={{
                                            p: 0.45,
                                            color: activeKeyframeAnimationId === animation.id ? "var(--app-text-primary)" : "var(--app-text-muted)",
                                            backgroundColor: activeKeyframeAnimationId === animation.id ? "rgba(43, 107, 253, 0.16)" : "transparent",
                                            "&:hover": { backgroundColor: "rgba(43, 107, 253, 0.24)" },
                                        }}
                                        title="Keyframes mode"
                                        onClick={() => handleToggleKeyframeModeStub(animation.id)}
                                    >
                                        <LayersOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0.45, color: "var(--app-text-muted)" }}
                                        title="Animation controls"
                                        onClick={(event) => handleOpenAnimationControls(animation.id, event)}
                                    >
                                        <TuneIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {animationError && (
                        <Box sx={{ px: 0.75, py: clipAnimations.length > 0 ? 0.25 : 0.75 }}>
                            <Typography sx={{ fontSize: 12, color: "#B42318" }}>{animationError}</Typography>
                        </Box>
                    )}
                </PaperPanel>
            )}

            <Popper
                open={Boolean(animationMenuAnchorEl)}
                anchorEl={animationMenuAnchorEl}
                placement="bottom-end"
                sx={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={() => setAnimationMenuAnchorEl(null)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: "10px", minWidth: 180 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <Box
                                onClick={handleCreateCustomAnimation}
                                sx={{
                                    ...transitionMenuItemSx,
                                    cursor: "pointer",
                                }}
                            >
                                <Typography sx={{ fontSize: 13, color: "var(--app-text-primary)" }}>Custom</Typography>
                            </Box>
                            <Box
                                sx={{
                                    ...transitionMenuItemSx,
                                    color: "var(--app-text-subtle)",
                                    cursor: "not-allowed",
                                    backgroundColor: "transparent",
                                    "&:hover": { backgroundColor: "transparent" },
                                }}
                                title="Coming soon"
                            >
                                <Typography sx={{ fontSize: 13, color: "var(--app-text-subtle)" }}>Zoom and Pan</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            <Popper
                open={Boolean(animationControlsAnchorEl && animationControlTarget)}
                anchorEl={animationControlsAnchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1302 }}
            >
                <ClickAwayListener onClickAway={closeAnimationControlsPanel}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 1.5,
                            borderRadius: "12px",
                            minWidth: 250,
                            boxShadow: "0 18px 40px rgba(0,0,0,0.16)",
                            border: "1px solid rgba(15, 15, 15, 0.06)",
                        }}
                    >
                        {animationControlTarget && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#222", lineHeight: 1.2 }}>
                                        {animationControlTarget.kind === "zoom_pan" ? "Zoom and Pan" : "Custom Animation"}
                                    </Typography>
                                    <IconButton size="small" onClick={closeAnimationControlsPanel} sx={{ p: 0.25, color: "var(--app-text-muted)" }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", minWidth: 42 }}>Start</Typography>
                                    <TextField
                                        size="small"
                                        value={animationStartDraft}
                                        onChange={(e) => setAnimationStartDraft(e.target.value)}
                                        onBlur={() => commitAnimationBoundary("start")}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                commitAnimationBoundary("start");
                                                (e.target as HTMLInputElement).blur();
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccessTimeIcon sx={{ fontSize: 16, color: "var(--app-text-subtle)" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            width: 150,
                                            "& .MuiInputBase-input": { fontSize: 14, fontWeight: 400, py: 0.55, textAlign: "left" },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", minWidth: 42 }}>End</Typography>
                                    <TextField
                                        size="small"
                                        value={animationEndDraft}
                                        onChange={(e) => setAnimationEndDraft(e.target.value)}
                                        onBlur={() => commitAnimationBoundary("end")}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                commitAnimationBoundary("end");
                                                (e.target as HTMLInputElement).blur();
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccessTimeIcon sx={{ fontSize: 16, color: "var(--app-text-subtle)" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            width: 150,
                                            "& .MuiInputBase-input": { fontSize: 14, fontWeight: 400, py: 0.55, textAlign: "left" },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", minWidth: 42 }}>Easing</Typography>
                                    <Select
                                        size="small"
                                        native
                                        value={animationControlTarget.easing}
                                        onChange={(e) => handleAnimationEasingChange(animationControlTarget.id, String(e.target.value))}
                                        inputProps={{ "aria-label": "Animation easing" }}
                                        sx={{ ...transitionSelectSx, width: 150 }}
                                    >
                                        {EASING_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Select>
                                </Box>

                                <Divider />

                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 500, minHeight: 40, py: 0.5 }}
                                    onClick={() => handleRemoveAnimation(animationControlTarget.id)}
                                >
                                    Remove
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Audio Effects (Audio/Video with audio) - Studio Sound toggle */}
            {hasAudioStream && (isAudioClip(clip) || isVideoClip(clip) || isScriptClip(clip)) && (
                <PaperPanel title="Audio effects">
                    <Box data-tour="studio-sound-toggle" sx={{px: 0.5, py: 0.5}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                            <Typography sx={{fontSize: 13}}>Studio Sound</Typography>
                            {showStudioSoundSpinner ? (
                                <Box
                                    data-tour="studio-sound-toggle"
                                    sx={{display: 'flex', alignItems: 'center', px: 1}}
                                    title={studioSoundSwitching ? 'Switching...' : 'Processing audio...'}
                                >
                                    <CircularProgress size={20} />
                                </Box>
                            ) : (
                                <Switch
                                    data-tour="studio-sound-toggle"
                                    checked={studioSound}
                                    onChange={(_, v) => handleStudioSoundToggle(v)}
                                    size="small"
                                    disabled={studioSoundSwitching}
                                    title={
                                        enhancedState.isSilent
                                            ? 'Silent audio - no effect'
                                            : (studioSound && (studioSoundPendingEnable || enhancedState.isQueued || enhancedState.isProcessing))
                                                ? 'Preparing enhanced audio...'
                                                : enhancedState.isFailed
                                                    ? 'Processing failed'
                                                    : 'Toggle AI-powered audio enhancement'
                                    }
                                />
                            )}
                        </Box>
                        <Typography sx={{fontSize: 11, color: 'text.secondary', mt: 0.5}}>
                            {enhancedState.isSilent
                                ? 'Silent audio - no effect'
                                : enhancedState.isFailed
                                    ? 'Processing failed. Toggle to retry.'
                                    : (studioSoundPendingEnable || enhancedState.isQueued || enhancedState.isProcessing) && studioSound
                                        ? 'Processing audio...'
                                        : 'Audio cleanup and noise reduction'}
                        </Typography>
                    </Box>
                </PaperPanel>
            )}
        </>
    );
});

export const V2PropertiesSidebar = () => {
    const [hasClip, setHasClip] = useState<boolean>(!!ApplicationStore.selectedClipId);
    const [headerTitle, setHeaderTitle] = useState<string>("Scene");
    useEffect(() => {
        const disposer = reaction(
            () => ({
                clipId: ApplicationStore.selectedClipId,
                sceneIndex: ApplicationStore.selectedSceneIndex,
            }),
            ({ clipId, sceneIndex }) => {
                setHasClip(!!clipId);
                if (!clipId) {
                    setHeaderTitle("Scene");
                    return;
                }
                try {
                    const clip: any = CompositionService.getClipById(clipId);
                    const isScript = String(clip?.type ?? "") === "script" || sceneIndex != null;
                    if (isScript) {
                        setHeaderTitle("Script");
                        return;
                    }
                    const name =
                        clip?.name ??
                        clip?.title ??
                        clip?.filename ??
                        clip?.fileName ??
                        clip?.id ??
                        "Clip";
                    setHeaderTitle(String(name));
                } catch {
                    setHeaderTitle("Clip");
                }
            },
            {fireImmediately: true}
        );
        return () => {
            disposer();
        };
    }, []);
    return (
        <Box
            data-tour="properties-sidebar-panel"
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                p: "3px",
            }}
        >
            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '8px'}}>
                {hasClip ? (
                    <>
                        <IconButton
                            size="small"
                            onClick={() => ApplicationStore.clearSelectedClipId()}
                            sx={{ p: 0.5, mr: 0.5, color: "var(--app-text-primary)" }}
                            aria-label="Back to scene"
                        >
                            <KeyboardBackspaceIcon fontSize="small" />
                        </IconButton>
                        <Box sx={titleTextStyle}>{headerTitle}</Box>
                    </>
                ) : (
                    <>
                        <Box sx={{width: '24px', height: '24px', color: "var(--app-text-primary)"}}>
                            <VideoStable/>
                        </Box>
                        <Box sx={titleTextStyle}>Scene</Box>
                    </>
                )}
            </Box>
            <PropertiesSidebarInner/>
        </Box>
    );
};

export default observer(V2PropertiesSidebar);
